import posthog from 'posthog-js'
import * as Sentry from '@sentry/nextjs'
import { getActiveGhostSession, getActiveLibraryGhostSession } from '@/store/ghostModeStore'
import { getInitialCanvasRecordingConfig } from './sessionRecordingPolicy'
import { REPLAY_MASKING_CONFIG } from './sessionRecordingMasking'
import { sanitizePostHogCaptureResult } from './telemetryUrlSanitizer'
import { createEventRateLimiter, isBenignBrowserNoise, RATE_LIMITED_EVENT } from './analyticsIngestFilters'
import type {
  CommentRefKind,
  FalConnectBeat,
  FalConnectLeaveReason,
  FalConnectScreen,
  OnboardingIntent,
} from '@/types/shared'
import type { ExperienceAnswer } from './onboarding/motionV2'
import type { LearnAssistantErrorKind } from './docs/assistantTelemetry'
import type { ProjectOpenOrigin } from './boot/openTiming'
import { PIXI_NO_STENCIL_MASK } from './engine/thumbnailRendererFlag'
import { consentRequired, readConsentChoice, storeConsentChoice } from './consent'
import { stripLocalePrefix } from '@/i18n/surfaces'

// Initialize PostHog only once
let initialized = false

/**
 * Circuit breaker against a runaway caller. Reports the trip once per event
 * name so the drop is visible rather than silent — deferred out of the
 * `before_send` call stack, since capturing from inside it would re-enter.
 */
const rateLimitCapture = createEventRateLimiter({
  onLimit: (event, limit) => {
    console.warn(`[Analytics] "${event}" exceeded ${limit} captures this session — dropping further events.`)
    setTimeout(() => {
      posthog.capture(RATE_LIMITED_EVENT, { limited_event: event, limit })
    }, 0)
  },
})

// localStorage key for storing the PostHog distinct_id before a reset.
// Used to link anonymous browsing activity to the authenticated profile after signup.
const POSTHOG_PRE_RESET_ID_KEY = 'ph_pre_reset_distinct_id'

export function initPostHog(): void {
  if (initialized || typeof window === 'undefined') return

  const isProduction = process.env.NODE_ENV === 'production'
  const apiKey = isProduction ? process.env.NEXT_PUBLIC_POSTHOG_KEY : devPostHogKey()
  if (!apiKey) {
    if (isProduction) console.warn('[Analytics] PostHog API key not configured')
    return
  }

  // Consent regions (EU/EEA/UK/CH) run cookieless and replay-free until the
  // visitor accepts; anonymous cookieless counts continue after a decline.
  // Needs "cookieless server hash mode" enabled in the PostHog project settings.
  const awaitingConsent = consentRequired() && readConsentChoice() !== 'granted'

  posthog.init(apiKey, {
    api_host: '/ingest',
    ui_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
    // Rate limit first so a dropped event skips sanitization entirely.
    before_send: [isBenignBrowserNoise, rateLimitCapture, sanitizePostHogCaptureResult],
    session_recording: {
      ...REPLAY_MASKING_CONFIG,
      captureCanvas: getInitialCanvasRecordingConfig(),
    },
    ...(awaitingConsent
      ? ({ cookieless_mode: 'on_reject', opt_out_capturing_by_default: true, disable_session_recording: true } as const)
      : {}),
  })

  initialized = true
}

// Local analytics testing only: set localStorage `mt_posthog_dev` to a project
// key (any `phc_…` string when requests are intercepted) to run PostHog in dev.
function devPostHogKey(): string | undefined {
  try {
    return localStorage.getItem('mt_posthog_dev') ?? undefined
  } catch {
    return undefined
  }
}

/** Visitor accepted analytics cookies: switch PostHog to normal capture and start replay. */
export function grantConsent(): void {
  storeConsentChoice('granted')
  if (!initialized) return
  posthog.opt_in_capturing()
  posthog.startSessionRecording()
}

/** Visitor declined: PostHog stays cookieless (anonymous counts only, no replay). */
export function denyConsent(): void {
  storeConsentChoice('denied')
  if (!initialized) return
  posthog.opt_out_capturing()
}

export function isAnalyticsEnabled(): boolean {
  return initialized
}

export function trackMotionGraphicEvent(
  event:
    | 'motion_graphic_exposure'
    | 'motion_graphic_editor_opened'
    | 'motion_graphic_preview_failed'
    | 'motion_graphic_source_edited'
    | 'motion_graphic_revision_requested',
  properties: Record<string, unknown> = {},
): void {
  if (!initialized) return
  posthog.capture(event, properties)
}

/**
 * Stop any in-progress session replay (PostHog AND Sentry).
 *
 * Used by the public phone capture route, whose URL hash carries the live
 * capture token. Both replay products snapshot the page URL independently of
 * their event `before_send` hooks, so the telemetry URL sanitizer alone cannot
 * scrub it — the capture shell calls this on mount so the token is never
 * recorded in a replay. The Sentry stop is intentionally NOT gated on PostHog's
 * `initialized` flag (Sentry inits separately, and replay fires at 100% on
 * error — likely on the capture flow's error paths).
 */
export function stopSessionReplay(): void {
  if (initialized) {
    try {
      posthog.stopSessionRecording()
    } catch {
      // Best-effort: never block the capture UI on analytics internals.
    }
  }
  try {
    void Promise.resolve(Sentry.getReplay()?.stop()).catch(() => {})
  } catch {
    // Best-effort: never block the capture UI on analytics internals.
  }
}

// ============================================================================
// User Identification
// ============================================================================

export function identifyUser(
  userId: string,
  properties?: {
    alias?: string
    isAnonymous?: boolean
    email?: string | null
  },
): void {
  if (!initialized) return

  posthog.identify(userId, {
    alias: properties?.alias,
    is_anonymous: properties?.isAnonymous,
    email: properties?.email ?? undefined,
  })
}

/**
 * Store the current PostHog distinct_id before an identity reset or OAuth redirect.
 * This allows linking anonymous browsing activity to the authenticated profile after signup.
 */
export function storePreResetIdentity(): void {
  if (!initialized || typeof window === 'undefined') return
  try {
    const currentId = posthog.get_distinct_id()
    if (currentId) {
      localStorage.setItem(POSTHOG_PRE_RESET_ID_KEY, currentId)
    }
  } catch {
    // Non-critical — don't break auth flow
  }
}

/**
 * After identifying an authenticated user, link any stored anonymous identity
 * so PostHog merges the anonymous and authenticated person profiles.
 * Call this only when the newly identified user is NOT anonymous.
 */
export function linkPreviousAnonymousIdentity(newUserId: string): void {
  if (!initialized || typeof window === 'undefined') return
  try {
    const previousId = localStorage.getItem(POSTHOG_PRE_RESET_ID_KEY)
    if (previousId) {
      if (previousId !== newUserId) {
        posthog.alias(newUserId, previousId)
      }
      // Remove after alias succeeds (or is skipped because IDs match).
      // If alias() throws, the ID stays in localStorage for a future retry.
      localStorage.removeItem(POSTHOG_PRE_RESET_ID_KEY)
    }
  } catch {
    // Non-critical — if alias fails, the ID remains for a retry on next page load
  }
}

/**
 * Clear any stored pre-reset identity (e.g., when a new anonymous session starts).
 * Prevents stale identities from being incorrectly linked to unrelated users.
 */
export function clearPreResetIdentity(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(POSTHOG_PRE_RESET_ID_KEY)
  } catch {
    // Non-critical
  }
}

export function resetUser(): void {
  if (!initialized) return
  // Store the current distinct_id so we can alias it after the next identify call.
  // This is the safety net — OAuth flows also call storePreResetIdentity() proactively
  // before redirecting, in case the page navigates before this handler fires.
  storePreResetIdentity()
  posthog.reset()
}

/**
 * Associate the current user with a PostHog group (e.g. a team).
 * This enables the Groups feature in PostHog so you can browse teams and see their members.
 */
export function setGroup(groupType: string, groupKey: string, properties?: Record<string, unknown>): void {
  if (!initialized) return
  posthog.group(groupType, groupKey, properties)
}

// ============================================================================
// Set (3D) Events (set_created stays on frontend)
// ============================================================================

export function trackSetCreated(props: {
  projectId: string
  setId: string
  source: 'upload' | 'canvas_drag' | 'generation_drag'
  imageFormat?: string
  creationMode?: 'fast' | 'quality'
}): void {
  if (!initialized) return
  posthog.capture('set_created', {
    project_id: props.projectId,
    set_id: props.setId,
    source: props.source,
    image_format: props.imageFormat,
    creation_mode: props.creationMode,
  })
}

export function trackSetViewerModeChanged(props: { projectId: string; setId: string; mode: 'camera' | 'place' }): void {
  if (!initialized) return
  posthog.capture('set_viewer_mode_changed', {
    project_id: props.projectId,
    set_id: props.setId,
    mode: props.mode,
  })
}

export function trackSetCharacterMoved(props: {
  projectId: string
  setId: string
  characterIndex: number
  transformType: 'translate' | 'rotate' | 'scale'
}): void {
  if (!initialized) return
  posthog.capture('set_character_moved', {
    project_id: props.projectId,
    set_id: props.setId,
    character_index: props.characterIndex,
    transform_type: props.transformType,
  })
}

export type SetViewerType = 'modal' | 'inline'

export function trackSetViewerOpened(props: { projectId: string; setId: string; viewerType: SetViewerType }): void {
  if (!initialized) return
  posthog.capture('set_viewer_opened', {
    project_id: props.projectId,
    set_id: props.setId,
    viewer_type: props.viewerType,
  })
}

export function trackSetViewerLoaded(props: {
  projectId: string
  setId: string
  viewerType: SetViewerType
  loadDurationMs: number
}): void {
  if (!initialized) return
  posthog.capture('set_viewer_loaded', {
    project_id: props.projectId,
    set_id: props.setId,
    viewer_type: props.viewerType,
    load_duration_ms: props.loadDurationMs,
  })
}

// ============================================================================
// Timeline Edit Events
// ============================================================================

interface ClipEventBase {
  clipId: string
  trackIndex: number
  projectId: string
}

export type ClipAssetType = 'video' | 'audio' | 'image' | 'sequence'

export function trackClipAdded(props: ClipEventBase & { assetType: ClipAssetType; isNestedSequence: boolean }): void {
  if (!initialized) return
  posthog.capture('clip_added', {
    clip_id: props.clipId,
    track_index: props.trackIndex,
    project_id: props.projectId,
    asset_type: props.assetType,
    is_nested_sequence: props.isNestedSequence,
  })
}

export function trackClipMoved(props: ClipEventBase): void {
  if (!initialized) return
  posthog.capture('clip_moved', {
    clip_id: props.clipId,
    track_index: props.trackIndex,
    project_id: props.projectId,
  })
}

export function trackClipResized(props: ClipEventBase): void {
  if (!initialized) return
  posthog.capture('clip_resized', {
    clip_id: props.clipId,
    track_index: props.trackIndex,
    project_id: props.projectId,
  })
}

export function trackClipDeleted(props: ClipEventBase): void {
  if (!initialized) return
  posthog.capture('clip_deleted', {
    clip_id: props.clipId,
    track_index: props.trackIndex,
    project_id: props.projectId,
  })
}

export function trackClipSplit(props: ClipEventBase): void {
  if (!initialized) return
  posthog.capture('clip_split', {
    clip_id: props.clipId,
    track_index: props.trackIndex,
    project_id: props.projectId,
  })
}

export function trackClipDuplicated(props: ClipEventBase): void {
  if (!initialized) return
  posthog.capture('clip_duplicated', {
    clip_id: props.clipId,
    track_index: props.trackIndex,
    project_id: props.projectId,
  })
}

// ============================================================================
// Playback Events
// ============================================================================

export type PlayerType = 'timeline' | 'canvas' | 'source' | 'program'

export function trackPlaybackStarted(props: { projectId: string; player: PlayerType; assetId?: string }): void {
  if (!initialized) return
  posthog.capture('playback_started', {
    project_id: props.projectId,
    player: props.player,
    asset_id: props.assetId ?? undefined,
  })
}

export function trackPlaybackStopped(props: {
  projectId: string
  player: PlayerType
  durationViewedSeconds: number
  assetId?: string
}): void {
  if (!initialized) return
  posthog.capture('playback_stopped', {
    project_id: props.projectId,
    player: props.player,
    duration_viewed_seconds: props.durationViewedSeconds,
    asset_id: props.assetId ?? undefined,
  })
}

// ============================================================================
// Upload Events
// ============================================================================

export type AssetType = 'video' | 'audio' | 'image' | 'set'

export function trackUploadStarted(props: {
  assetType: AssetType
  fileSizeBytes: number
  mimeType: string
  /** Pre-normalization MIME (e.g. image/heic, image/avif) when the upload was converted client-side. */
  originalMimeType?: string
  durationSeconds?: number
  projectId: string
}): void {
  if (!initialized) return
  posthog.capture('upload_started', {
    asset_type: props.assetType,
    file_size_bytes: props.fileSizeBytes,
    mime_type: props.mimeType,
    original_mime_type: props.originalMimeType ?? undefined,
    duration_seconds: props.durationSeconds,
    project_id: props.projectId,
  })
}

export function trackUploadCompleted(props: {
  assetId: string
  assetType: AssetType
  uploadTimeMs: number
  status: 'completed' | 'failed'
  errorCode?: string
}): void {
  if (!initialized) return
  posthog.capture('upload_completed', {
    asset_id: props.assetId,
    asset_type: props.assetType,
    upload_time_ms: props.uploadTimeMs,
    status: props.status,
    error_code: props.errorCode,
  })
}

// ============================================================================
// Download Events
// ============================================================================

export type DownloadSource = 'detail_panel' | 'context_menu' | 'expanded_modal'

export function trackDownloadStarted(props: {
  assetId: string
  assetType: AssetType
  source: DownloadSource
  projectId?: string
  hasEnhanced?: boolean
}): void {
  if (!initialized) return
  posthog.capture('download_started', {
    asset_id: props.assetId,
    asset_type: props.assetType,
    source: props.source,
    project_id: props.projectId,
    has_enhanced: props.hasEnhanced ?? undefined,
  })
}

export function trackDownloadCompleted(props: {
  assetId: string
  assetType: AssetType
  source: DownloadSource
  projectId?: string
  status: 'completed' | 'failed'
  error?: string
  downloadTimeMs: number
}): void {
  if (!initialized) return
  posthog.capture('download_completed', {
    asset_id: props.assetId,
    asset_type: props.assetType,
    source: props.source,
    project_id: props.projectId,
    status: props.status,
    error: props.error ?? undefined,
    download_time_ms: props.downloadTimeMs,
  })
}

/**
 * A download was delivered carrying the unpaid-org Martini mark. This is the
 * EXPOSURE denominator for the download watermark upsell: the upsell modal
 * opens at most once per session, so topup_modal_opened
 * (trigger=download_watermark) undercounts how many marked files actually went
 * out. `upsell_shown` says whether this delivery opened the modal or the
 * once-per-session gate had already fired.
 */
export function trackDownloadWatermarked(props: {
  assetId: string
  projectId?: string
  billingMode?: string
  upsellShown: boolean
}): void {
  if (!initialized) return
  posthog.capture('download_watermarked', {
    asset_id: props.assetId,
    project_id: props.projectId ?? undefined,
    billing_mode: props.billingMode ?? undefined,
    upsell_shown: props.upsellShown,
  })
}

// ============================================================================
// Project Lifecycle Events
// ============================================================================

export function trackProjectDeleted(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('project_deleted', {
    project_id: props.projectId,
  })
}

export function trackProjectDuplicateClicked(props: { sourceProjectId: string }): void {
  if (!initialized) return
  posthog.capture('project_duplicate_clicked', {
    source_project_id: props.sourceProjectId,
  })
}

export function trackProjectDuplicated(props: { sourceProjectId: string; newProjectId: string }): void {
  if (!initialized) return
  posthog.capture('project_duplicated', {
    source_project_id: props.sourceProjectId,
    new_project_id: props.newProjectId,
  })
}

// ============================================================================
// Welcome / Onboarding Events
// ============================================================================

export function trackWelcomePageViewed(): void {
  if (!initialized) return
  posthog.capture('welcome_page_viewed')
}

export function trackTeamWelcomePageViewed(props: { teamSlug: string }): void {
  if (!initialized) return
  posthog.capture('team_welcome_page_viewed', {
    team_slug: props.teamSlug,
  })
}

export function trackQuickStartProjectCreated(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('quick_start_project_created', {
    project_id: props.projectId,
  })
}

export function trackSampleProjectCreated(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('sample_project_created', {
    project_id: props.projectId,
  })
}

// ============================================================================
// Billing Events
// ============================================================================

export function trackBillingPageViewed(): void {
  if (!initialized) return
  posthog.capture('billing_page_viewed')
}

export function trackMcpUsageMeterViewed(props: { organizationId: string; used: number; limit: number }): void {
  if (!initialized) return
  posthog.capture('mcp_usage_meter_viewed', {
    organization_id: props.organizationId,
    used: props.used,
    limit: props.limit,
    $groups: { team: props.organizationId },
  })
}

/**
 * The billing page's collapsed olive store was expanded. `auto` distinguishes
 * conversion-default opens (zero balance, post-checkout return) from explicit
 * "Top up" clicks — compare against topup_checkout_started to see which
 * entry converts.
 */
export function trackTopupStoreOpened(props: { auto: boolean; balance: number | null }): void {
  if (!initialized) return
  posthog.capture('topup_store_opened', {
    auto: props.auto,
    balance: props.balance ?? undefined,
  })
}

/**
 * The Pro option appeared for a free user starting a top-up. Pairs with
 * plan_checkout_started (source billing_page → picked Pro) and
 * topup_checkout_started (→ picked the plain top-up) for the fork funnel.
 * `surface` says which form it took: the pre-Stripe fork dialog (no saved
 * card) or the checkbox on the saved-card confirm sheet.
 */
export function trackTopupProUpsellShown(props: {
  packOlives: number
  promoEligible: boolean
  source?: string
  surface?: 'fork' | 'confirm'
}): void {
  if (!initialized) return
  posthog.capture('topup_pro_upsell_shown', {
    pack_olives: props.packOlives,
    promo_eligible: props.promoEligible,
    source: props.source ?? undefined,
    surface: props.surface ?? undefined,
  })
}

/** Pricing-page calculator input changed — measures whether the tool earns its slot. */
export function trackPricingCalculatorInteracted(props: {
  modelKey: string
  variantLabel: string
  seconds?: number
  unit: string
}): void {
  if (!initialized) return
  posthog.capture('pricing_calculator_interacted', {
    model_key: props.modelKey,
    variant_label: props.variantLabel,
    seconds: props.seconds,
    unit: props.unit,
  })
}

// ============================================================================
// Marketing site
// ============================================================================

/** The marketing path without its locale prefix (`/ja/pricing` → `/pricing`): every marketing event's `page`. */
function marketingPage(): string {
  return stripLocalePrefix(window.location.pathname).pathname
}

// "Once per page view" events; MarketingTracker clears this on every route change.
const marketingSeen = new Set<string>()
function firstThisPageView(key: string): boolean {
  if (marketingSeen.has(key)) return false
  marketingSeen.add(key)
  return true
}

export function startMarketingPageView(): void {
  marketingSeen.clear()
}

/** A marketing section was ≥40% on screen (once per section per page view). */
export function trackMarketingSectionViewed(props: { section: string }): void {
  if (!initialized || !firstThisPageView(`section:${props.section}`)) return
  posthog.capture('marketing_section_viewed', { section: props.section, page: marketingPage() })
}

/** Any marketing call to action. `outlet` is set for press articles, `target` for compare links. */
export function trackMarketingCtaClicked(props: {
  cta: string
  location: string
  outlet?: string
  target?: string
}): void {
  if (!initialized) return
  posthog.capture('marketing_cta_clicked', {
    cta: props.cta,
    location: props.location,
    outlet: props.outlet,
    target: props.target,
    page: marketingPage(),
  })
}

/** Homepage scroll depth, 25/50/75/100 (once each per page view). */
export function trackMarketingScrollDepth(props: { depthPercent: number }): void {
  if (!initialized || !firstThisPageView(`depth:${props.depthPercent}`)) return
  posthog.capture('marketing_scroll_depth', { depth_percent: props.depthPercent, page: marketingPage() })
}

/** A homepage production feature became the active one (once per feature per page view). */
export function trackMarketingFeatureViewed(props: { feature: string }): void {
  if (!initialized || !firstThisPageView(`feature:${props.feature}`)) return
  posthog.capture('marketing_feature_viewed', { feature: props.feature, page: marketingPage() })
}

export function trackMarketingFeatureNavClicked(props: { feature: string }): void {
  if (!initialized) return
  posthog.capture('marketing_feature_nav_clicked', { feature: props.feature, page: marketingPage() })
}

/** How far the agents diagram's scroll choreography got: 25/50/75/100 (once each per page view). */
export function trackMarketingDiagramProgress(props: { milestone: number }): void {
  if (!initialized || !firstThisPageView(`diagram:${props.milestone}`)) return
  posthog.capture('marketing_diagram_progress', { milestone: props.milestone, page: marketingPage() })
}

/** First hands-on interaction with a marketing demo (once per demo per page view). */
export function trackMarketingDemoInteracted(props: { demo: string; action: string; tab?: string }): void {
  if (!initialized || !firstThisPageView(`demo:${props.demo}`)) return
  posthog.capture('marketing_demo_interacted', {
    demo: props.demo,
    action: props.action,
    tab: props.tab,
    page: marketingPage(),
  })
}

export function trackMarketingFaqOpened(props: { questionId: string }): void {
  if (!initialized) return
  posthog.capture('marketing_faq_opened', { question_id: props.questionId, page: marketingPage() })
}

export function trackFooterLinkClicked(props: { label: string; column: string; href: string }): void {
  if (!initialized) return
  posthog.capture('footer_link_clicked', {
    label: props.label,
    column: props.column,
    href: props.href,
    page: marketingPage(),
  })
}

export function trackPricingFalKeyToggled(props: { on: boolean }): void {
  if (!initialized) return
  posthog.capture('pricing_fal_key_toggled', { on: props.on, page: marketingPage() })
}

export function trackPricingCompareOpened(): void {
  if (!initialized) return
  posthog.capture('pricing_compare_opened', { page: marketingPage() })
}

export function trackPricingCompareTableSwitched(props: { table: 'standard' | 'byok' }): void {
  if (!initialized) return
  posthog.capture('pricing_compare_table_switched', { table: props.table, page: marketingPage() })
}

export function trackPricingModelPricesExpanded(props: { group: string }): void {
  if (!initialized) return
  posthog.capture('pricing_model_prices_expanded', { group: props.group, page: marketingPage() })
}

export function trackComparePageViewed(props: { competitor: string }): void {
  if (!initialized) return
  posthog.capture('compare_page_viewed', { competitor: props.competitor, page: marketingPage() })
}

export function trackPodcastEpisodePlayed(props: { episode: number; videoId: string }): void {
  if (!initialized) return
  posthog.capture('podcast_episode_played', { episode: props.episode, video_id: props.videoId, page: marketingPage() })
}

// ── AI Film Budget (/production-calculator) ─────────────────────────────────────────

export type FilmBudgetInputSource = 'slider' | 'number' | 'preset' | 'reset' | 'segment' | 'toggle'
export type FilmBudgetSection = 'project' | 'generation' | 'personnel' | 'cleanup' | 'summary'
export type FilmBudgetExportKind = 'xlsx' | 'link' | 'summary'

/** Page open. `sharedLink` is true when the URL carried any budget params — arrivals on shared links are the signal. */
export function trackFilmBudgetViewed(props: { sharedLink: boolean; paramKeys: string[] }): void {
  if (!initialized) return
  posthog.capture('film_budget_viewed', {
    shared_link: props.sharedLink,
    param_keys: props.paramKeys,
  })
}

/** One input committed (end of drag, blur of a typed number, chip, reset) — never per tick. */
export function trackFilmBudgetInputChanged(props: {
  param: string
  from: number | string | boolean
  to: number | string | boolean
  source: FilmBudgetInputSource
  totalUsd: number
}): void {
  if (!initialized) return
  posthog.capture('film_budget_input_changed', {
    param: props.param,
    from: props.from,
    to: props.to,
    source: props.source,
    total_usd: Math.round(props.totalUsd),
  })
}

/** A section scrolled into the middle of the viewport (once per section per page view). */
export function trackFilmBudgetSectionViewed(props: { section: FilmBudgetSection }): void {
  if (!initialized) return
  posthog.capture('film_budget_section_viewed', { section: props.section })
}

/** Download Excel / Copy link / Copy summary — the moment a budget leaves the page. */
export function trackFilmBudgetExported(props: {
  kind: FilmBudgetExportKind
  totalUsd: number
  aiMinutes: number
}): void {
  if (!initialized) return
  posthog.capture('film_budget_exported', {
    kind: props.kind,
    total_usd: Math.round(props.totalUsd),
    ai_minutes: Math.round(props.aiMinutes),
  })
}

export function trackTopupModalOpened(props: {
  requiredCost: number
  currentBalance: number
  shortfall: number
  billingMode?: string
  cardShown: boolean
  /**
   * What opened the modal: 'generate' = user pressed Generate while short (the
   * press-through flow — auto-resumes after payment), 'topup_link' = the inline
   * "Top up" link next to the shortfall notice, 'download_watermark' = a
   * never-paid org just downloaded a marked file (no shortfall — shortfall is 0
   * on this trigger — and paying makes every download clean, so it auto-resumes
   * the download). Absent on legacy openers (canvas-node retries, global store).
   */
  trigger?: 'generate' | 'topup_link' | 'download_watermark'
}): void {
  if (!initialized) return
  posthog.capture('topup_modal_opened', {
    required_cost: props.requiredCost,
    current_balance: props.currentBalance,
    shortfall: props.shortfall,
    billing_mode: props.billingMode ?? undefined,
    card_shown: props.cardShown,
    trigger: props.trigger ?? undefined,
  })
}

/**
 * A generation blocked by an olive shortfall auto-fired after the user topped
 * up in the insufficient-credits modal (saved-card path). The last leg of the
 * press-through funnel: topup_modal_opened(trigger=generate) → topup_completed
 * → topup_generation_resumed.
 */
export function trackTopupGenerationResumed(props: { modality: 'video' | 'image'; estimatedCost: number }): void {
  if (!initialized) return
  posthog.capture('topup_generation_resumed', {
    modality: props.modality,
    estimated_cost: props.estimatedCost,
  })
}

/**
 * The marked download auto-re-fired clean after the user topped up in the
 * watermark upsell (saved-card path — the Stripe-redirect path navigates away
 * and cannot resume). Last leg of the watermark funnel: download_watermarked →
 * topup_modal_opened(trigger=download_watermark) →
 * topup_completed(source=watermark_upsell_modal) → this.
 */
export function trackTopupDownloadResumed(props: { billingMode?: string }): void {
  if (!initialized) return
  posthog.capture('topup_download_resumed', {
    billing_mode: props.billingMode ?? undefined,
  })
}

export function trackTopupPackSelected(props: {
  packAmountUsd: number
  olives: number
  source?: string
  /** Plan-tier bonus olives credited on top of the face quantity (0/absent when no bonus applies) */
  bonusOlives?: number
  /** Bonus percent for the buyer's plan tier (Pro 3, Studio 5) */
  tierPct?: number
}): void {
  if (!initialized) return
  posthog.capture('topup_pack_selected', {
    pack_amount_usd: props.packAmountUsd,
    olives: props.olives,
    source: props.source,
    face_olives: props.olives,
    bonus_olives: props.bonusOlives || undefined,
    tier_pct: props.tierPct || undefined,
  })
}

export function trackTopupCheckoutStarted(props: {
  amountUsd: number
  olives: number
  source?: string
  /** Plan-tier bonus olives credited on top of the face quantity (0/absent when no bonus applies) */
  bonusOlives?: number
  /** Bonus percent for the buyer's plan tier (Pro 3, Studio 5) */
  tierPct?: number
}): void {
  if (!initialized) return
  posthog.capture('topup_checkout_started', {
    amount_usd: props.amountUsd,
    olives: props.olives,
    source: props.source,
    face_olives: props.olives,
    bonus_olives: props.bonusOlives || undefined,
    tier_pct: props.tierPct || undefined,
  })
}

export function trackTopupCancelled(props: { amountUsd: number; olives: number }): void {
  if (!initialized) return
  posthog.capture('topup_cancelled', {
    amount_usd: props.amountUsd,
    olives: props.olives,
  })
}

export function trackTopupRebuyShown(props: { amountUsd: number; olives: number; source?: string }): void {
  if (!initialized) return
  posthog.capture('topup_rebuy_shown', {
    amount_usd: props.amountUsd,
    olives: props.olives,
    source: props.source,
  })
}

export function trackTopupRebuyConfirmed(props: { amountUsd: number; olives: number; source?: string }): void {
  if (!initialized) return
  posthog.capture('topup_rebuy_confirmed', {
    amount_usd: props.amountUsd,
    olives: props.olives,
    source: props.source,
  })
}

export function trackTopupRebuyFailed(props: {
  amountUsd: number
  olives: number
  errorCode?: string
  source?: string
}): void {
  if (!initialized) return
  posthog.capture('topup_rebuy_failed', {
    amount_usd: props.amountUsd,
    olives: props.olives,
    error_code: props.errorCode,
    source: props.source,
  })
}

// ============================================================================
// Subscription Events
// ============================================================================

export function trackPlanSelected(props: { planId: string; source: string }): void {
  if (!initialized) return
  posthog.capture('plan_selected', {
    plan_id: props.planId,
    source: props.source,
  })
}

export function trackPlanUpgradeModalOpened(props: {
  currentPlan: string | null | undefined
  orgType: string
  orgId?: string
}): void {
  if (!initialized) return
  posthog.capture('plan_upgrade_modal_opened', {
    current_plan: props.currentPlan,
    org_type: props.orgType,
    org_id: props.orgId,
  })
}

export function trackPlanCheckoutStarted(props: {
  planId: string
  orgType: string
  orgId?: string
  source?: string
  /** Funnel flow marker, e.g. 'onboarding_v2' when checkout starts from the onboarding stepper. */
  flow?: string
  extraOlives?: number
  /** Whether the 50%-off-first-month promo was shown for this checkout (render-time eligibility) */
  firstMonthDiscount?: boolean
}): void {
  if (!initialized) return
  posthog.capture('plan_checkout_started', {
    plan_id: props.planId,
    org_type: props.orgType,
    org_id: props.orgId,
    source: props.source,
    flow: props.flow,
    extra_olives: props.extraOlives,
    first_month_discount: props.firstMonthDiscount ?? undefined,
  })
}

/**
 * 50%-off-first-month price shown to an eligible user. Fired once per surface
 * mount — compare against subscription_confirmed and Stripe coupon
 * redemptions to audit display/charge mismatch.
 */
export function trackFirstMonthPromoShown(props: { planId: string; source: string }): void {
  if (!initialized) return
  posthog.capture('first_month_promo_shown', {
    plan_id: props.planId,
    source: props.source,
  })
}

// ============================================================================
// Team Events
// ============================================================================

export function trackTeamCheckoutStarted(props: { teamName: string; planId: string; source: string }): void {
  if (!initialized) return
  posthog.capture('team_checkout_started', {
    team_name: props.teamName,
    plan_id: props.planId,
    source: props.source,
  })
}

export function trackTeamDeleted(props: { teamId: string }): void {
  if (!initialized) return
  posthog.capture('team_deleted', {
    team_id: props.teamId,
  })
}

// ============================================================================
// Referral Events
// ============================================================================

export function trackInvitesPageViewed(props: { invitesUsed: number; invitesRemaining: number }): void {
  if (!initialized) return
  posthog.capture('invites_page_viewed', {
    invites_used: props.invitesUsed,
    invites_remaining: props.invitesRemaining,
  })
}

export function trackInviteLinkCopied(props: { source: 'page' | 'dropdown' }): void {
  if (!initialized) return
  posthog.capture('invite_link_copied', {
    source: props.source,
  })
}

export function trackInvitePageViewed(props: { referrerCode: string }): void {
  if (!initialized) return
  posthog.capture('invite_page_viewed', {
    referrer_code: props.referrerCode,
  })
}

export function trackInviteGetStartedClicked(): void {
  if (!initialized) return
  posthog.capture('invite_get_started_clicked')
}

// referral_signup_completed is emitted server-side in the complete-conversion endpoint

/**
 * Anonymous or logged-in visitor landed on a shared project link — the middle
 * leg of the share funnel (share_link_copied → project_link_visited → signup_completed).
 */
export function trackProjectLinkVisited(props: {
  projectId: string
  hasRef: boolean
  isAnonymous: boolean
  isMobile?: boolean
}): void {
  if (!initialized) return
  posthog.capture('project_link_visited', {
    project_id: props.projectId,
    has_ref: props.hasRef,
    is_anonymous: props.isAnonymous,
    is_mobile: props.isMobile ?? false,
  })
}

// ============================================================================
// Marketing Page Events
// ============================================================================

export function trackPricingPageViewed(): void {
  if (!initialized) return
  posthog.capture('pricing_page_viewed')
}

// ============================================================================
// Account Dropdown Events
// ============================================================================

export function trackAccountDropdownOpened(): void {
  if (!initialized) return
  posthog.capture('account_dropdown_opened')
}

export function trackAccountDropdownBalanceClicked(props: { currentBalance: number }): void {
  if (!initialized) return
  posthog.capture('account_dropdown_balance_clicked', {
    current_balance: props.currentBalance,
  })
}

// ============================================================================
// UI Events
// ============================================================================

export type EditorMode = 'create' | 'edit'

export function trackModeSwitch(props: { mode: EditorMode; projectId: string }): void {
  if (!initialized) return
  posthog.capture('mode_switched', {
    mode: props.mode,
    project_id: props.projectId,
  })
}

export function trackBrowserWarningViewed(props: {
  pathname: string
  browser: string
  engine: string
  os: string
  platform: string
}): void {
  if (!initialized) return
  posthog.capture('browser_warning_viewed', {
    pathname: props.pathname,
    browser: props.browser,
    engine: props.engine,
    os: props.os,
    platform: props.platform,
  })
}

/**
 * Prompt lint (ENG-882): the advisory Jev pre-flight under the prompt. `codes`
 * are the fixed warning codes shown; the prompt text itself is never sent.
 */
export function trackPromptLintWarningShown(props: {
  modality: 'video' | 'image' | 'audio'
  modelId: string
  codes: string[]
  promptLength: number
}): void {
  if (!initialized) return
  posthog.capture('prompt_lint_warning_shown', {
    modality: props.modality,
    model_id: props.modelId,
    codes: props.codes,
    code_count: props.codes.length,
    prompt_length: props.promptLength,
  })
}

export function trackPromptLintWarningDismissed(props: {
  modality: 'video' | 'image' | 'audio'
  modelId: string
  codes: string[]
  promptLength: number
}): void {
  if (!initialized) return
  posthog.capture('prompt_lint_warning_dismissed', {
    modality: props.modality,
    model_id: props.modelId,
    codes: props.codes,
    code_count: props.codes.length,
    prompt_length: props.promptLength,
  })
}

/**
 * Model fit (ENG-883): the user picked a model after Jev scored the brief on
 * screen. In shadow nothing was shown, so this is the baseline pick. `rank` is
 * the model's 1-based position in the ranking when the backend is active (null
 * in shadow or when the hard filter excluded it); the brief text is never sent.
 */
export function trackModelFitModelPicked(props: {
  modality: 'video' | 'image' | 'audio'
  mode: 'shadow' | 'active'
  /** Pairs this pick with the backend `model_fit_scored` event that ranked the same brief. */
  fitId: string
  modelId: string
  rank: number | null
  level: string | null
  rankedCount: number
  questionVersion: number
}): void {
  if (!initialized) return
  posthog.capture('model_fit_model_picked', {
    modality: props.modality,
    mode: props.mode,
    fit_id: props.fitId,
    model_id: props.modelId,
    rank: props.rank,
    level: props.level,
    ranked_count: props.rankedCount,
    question_version: props.questionVersion,
  })
}

export function trackBrowserWarningDismissed(props: {
  pathname: string
  browser: string
  engine: string
  os: string
  platform: string
}): void {
  if (!initialized) return
  posthog.capture('browser_warning_dismissed', {
    pathname: props.pathname,
    browser: props.browser,
    engine: props.engine,
    os: props.os,
    platform: props.platform,
  })
}

// ============================================================================
// Onboarding Funnel Events
// ============================================================================

export type SignupModalVariant = 'marketing' | 'project'
export type SignupModalMode = 'signup' | 'signin'
// `hero` / `nav` / `pricing_cta`: which marketing CTA opened the modal.
export type SignupSource = 'default' | 'motion_share' | 'hero' | 'nav' | 'pricing_cta'

export function trackSignupModalViewed(props: {
  variant: SignupModalVariant
  mode: SignupModalMode
  source?: SignupSource
}): void {
  if (!initialized) return
  posthog.capture('signup_modal_viewed', {
    variant: props.variant,
    mode: props.mode,
    source: props.source ?? 'default',
  })
}

// signup_completed event is now emitted server-side in completeConversion endpoint

export type SignupStep = 'email_entry' | 'otp_verification' | 'password_entry' | 'waitlist_gate'

export function trackSignupStepViewed(props: { step: SignupStep; source?: SignupSource }): void {
  if (!initialized) return
  posthog.capture('signup_step_viewed', {
    step: props.step,
    source: props.source ?? 'default',
  })
}

export function trackSignupStepCompleted(props: { step: SignupStep; source?: SignupSource }): void {
  if (!initialized) return
  // Delivered outside the batch queue, over sendBeacon. The final step completes
  // microseconds before the modal tears down, the caller navigates (router.replace
  // / window.location.reload) and the anonymous -> real session swap fires
  // SIGNED_OUT -> posthog.reset(). A queued event does not survive that, which
  // undercounted password_entry by ~80% (88 captured against 441 server-confirmed
  // signups) and made a 92%-converting step look like an 82% drop-off.
  //
  // send_instantly alone is not enough where the caller reloads synchronously
  // (auth/mcp/authorize): compression is on by default, and the gzip path runs
  // through CompressionStream asynchronously, so a reload can discard the payload
  // before the request is ever dispatched. posthog only takes the synchronous
  // Base64 + navigator.sendBeacon path when the transport is pinned here, and the
  // browser then owns delivery across the unload.
  //
  // Applied to every step rather than just the terminal one -- three small beacons
  // per signup is not worth branching over.
  posthog.capture(
    'signup_step_completed',
    {
      step: props.step,
      source: props.source ?? 'default',
    },
    { send_instantly: true, transport: 'sendBeacon' },
  )
}

export type SignupErrorCode =
  | 'turnstile_load_failed'
  | 'turnstile_timeout'
  | 'turnstile_rejected'
  | 'otp_invalid'
  | 'otp_expired'
  | 'otp_too_many_attempts'
  | 'send_otp_failed'
  | 'password_set_failed'
  | 'google_start_failed'

export function trackSignupError(props: { step: SignupStep; code: SignupErrorCode; source?: SignupSource }): void {
  if (!initialized) return
  posthog.capture('signup_error', {
    step: props.step,
    code: props.code,
    source: props.source ?? 'default',
  })
}

/** The signup form silently switched to sign-in because the email already has an account. */
export function trackSignupExistingAccountSwitched(props?: { source?: SignupSource }): void {
  if (!initialized) return
  posthog.capture('signup_existing_account_switched', {
    source: props?.source ?? 'default',
  })
}

export function trackEditorOpened(props: {
  projectId: string
  /** True when the editor was opened behind the mobile wall (honest-opens contract C10). */
  blocked?: boolean
}): void {
  if (!initialized) return
  posthog.capture('editor_opened', {
    project_id: props.projectId,
    blocked: props.blocked,
  })
}

export function trackShareLinkCopied(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('share_link_copied', {
    project_id: props.projectId,
  })
}

// ============================================================================
// Camera Motion Share Events (standalone /m/<id> before/after share page)
// ============================================================================
// Funnel terminates in activation, not page engagement:
//   motion_share_viewed → signup_completed[source=motion_share] → activation_generation_completed
// The metric that matters: net activated signups per 100 share views.

/** Editor: creator published a render and copied its share link. */
export function trackMotionShareLinkCopied(props: { motionId: string }): void {
  if (!initialized) return
  posthog.capture('motion_share_link_copied', { motion_id: props.motionId })
}

/** Editor: a render's published state was toggled. */
export function trackMotionPublished(props: { motionId: string; published: boolean }): void {
  if (!initialized) return
  posthog.capture(props.published ? 'motion_published' : 'motion_unpublished', { motion_id: props.motionId })
}

/** Editor: creator pushed a share to a specific platform (or copied the caption). */
export function trackMotionShared(props: { motionId: string; platform: string }): void {
  if (!initialized) return
  posthog.capture('motion_shared', { motion_id: props.motionId, platform: props.platform })
}

/** Public page: a visitor loaded a share (middle leg of the funnel). */
export function trackMotionShareViewed(props: {
  motionId: string
  isAnonymous?: boolean
  isMobile?: boolean
  authResolved?: boolean
}): boolean {
  if (!initialized) return false
  posthog.capture('motion_share_viewed', {
    motion_id: props.motionId,
    is_anonymous: props.isAnonymous ?? undefined,
    is_mobile: props.isMobile ?? false,
    auth_resolved: props.authResolved ?? true,
  })
  return true
}

/** Public page: auth finished after an immediate provisional share view. */
export function trackMotionShareAuthResolved(props: {
  motionId: string
  isAnonymous: boolean
  isMobile?: boolean
}): void {
  if (!initialized) return
  posthog.capture('motion_share_auth_resolved', {
    motion_id: props.motionId,
    is_anonymous: props.isAnonymous,
    is_mobile: props.isMobile ?? false,
    auth_resolved: true,
  })
}

/** Public page: first interaction with the before/after slider (once per view). */
export function trackMotionShareSliderUsed(props: { motionId: string; isAnonymous: boolean }): void {
  if (!initialized) return
  posthog.capture('motion_share_slider_used', { motion_id: props.motionId, is_anonymous: props.isAnonymous })
}

/** Public page: clicked the "make your own" CTA (feeds signup_completed). */
export type MotionShareCtaLocation = 'header' | 'main' | 'mobile_sticky'
export type MotionShareAuthState = 'loading' | 'anonymous' | 'signed_in'

export function trackMotionShareCtaClicked(props: {
  motionId: string
  isAnonymous: boolean
  ctaLocation: MotionShareCtaLocation
  authState: MotionShareAuthState
  returnPath?: string
}): void {
  if (!initialized) return
  posthog.capture('motion_share_cta_clicked', {
    motion_id: props.motionId,
    is_anonymous: props.isAnonymous,
    cta_location: props.ctaLocation,
    auth_state: props.authState,
    return_path: props.returnPath ?? undefined,
  })
}

// ============================================================================
// Camera Motion Handheld Capture Events
// ============================================================================

export type CameraCaptureFunnelEvent =
  | 'camera_capture_affordance_viewed'
  | 'camera_capture_connect_phone_opened'
  | 'camera_capture_qr_shown'
  | 'camera_capture_claim_requested'
  | 'camera_capture_accepted'
  | 'camera_capture_magic_window_ready'
  | 'camera_capture_armed'
  | 'camera_capture_take_recorded'
  | 'camera_capture_take_applied'
  | 'camera_capture_take_kept'
  | 'camera_capture_handheld_rendered'

export type CameraCaptureWebXrCapabilityStatus = 'supported' | 'unsupported' | 'unavailable' | 'check_failed'

export interface CameraCaptureDeviceMetadata {
  devicePlatform?: string
  deviceType?: string
  browserName?: string
  browserVersion?: string
  osName?: string
  osVersion?: string
}

export interface CameraCaptureDiagnosticsBase extends CameraCaptureDeviceMetadata {
  sessionId?: string
  holdout?: boolean
  betaEnabled?: boolean
}

function cameraCaptureDiagnosticsProperties(props: CameraCaptureDiagnosticsBase): Record<string, unknown> {
  return {
    session_id: props.sessionId ?? undefined,
    device_platform: props.devicePlatform ?? undefined,
    device_type: props.deviceType ?? undefined,
    browser_name: props.browserName ?? undefined,
    browser_version: props.browserVersion ?? undefined,
    os_name: props.osName ?? undefined,
    os_version: props.osVersion ?? undefined,
    beta_enabled: props.betaEnabled ?? undefined,
    holdout: props.holdout ?? undefined,
  }
}

export function trackCameraCaptureFunnelEvent(
  event: CameraCaptureFunnelEvent,
  props: {
    projectId?: string
    sourceAssetId?: string
    sessionId?: string
    takeId?: string
    devicePlatform?: string
    captureMode?: string
    holdout?: boolean
    durationSeconds?: number
    sampleCount?: number
  },
): void {
  if (!initialized) return
  posthog.capture(event, {
    project_id: props.projectId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    session_id: props.sessionId ?? undefined,
    take_id: props.takeId ?? undefined,
    device_platform: props.devicePlatform ?? undefined,
    capture_mode: props.captureMode ?? undefined,
    holdout: props.holdout ?? undefined,
    duration_seconds: props.durationSeconds ?? undefined,
    sample_count: props.sampleCount ?? undefined,
  })
}

export function trackCameraCaptureWebXrCapabilityChecked(
  props: CameraCaptureDiagnosticsBase & {
    status: CameraCaptureWebXrCapabilityStatus
  },
): void {
  if (!initialized) return
  posthog.capture('camera_capture_webxr_capability_checked', {
    ...cameraCaptureDiagnosticsProperties(props),
    status: props.status,
  })
}

export function trackCameraCaptureWebXrUnsupportedStateShown(
  props: CameraCaptureDiagnosticsBase & {
    reason: string
  },
): void {
  if (!initialized) return
  posthog.capture('camera_capture_webxr_unsupported_state_shown', {
    ...cameraCaptureDiagnosticsProperties(props),
    reason: props.reason,
  })
}

export function trackCameraCaptureRecordingStarted(
  props: CameraCaptureDiagnosticsBase & {
    captureMode?: string
  },
): void {
  if (!initialized) return
  posthog.capture('camera_capture_recording_started', {
    ...cameraCaptureDiagnosticsProperties(props),
    capture_mode: props.captureMode ?? undefined,
  })
}

export function trackCameraCaptureTakeUploadFailed(
  props: CameraCaptureDiagnosticsBase & {
    takeId?: string
    captureMode?: string
    durationSeconds?: number
    sampleCount?: number
    failureReason: string
  },
): void {
  if (!initialized) return
  posthog.capture('camera_capture_take_upload_failed', {
    ...cameraCaptureDiagnosticsProperties(props),
    take_id: props.takeId ?? undefined,
    capture_mode: props.captureMode ?? undefined,
    duration_seconds: props.durationSeconds ?? undefined,
    sample_count: props.sampleCount ?? undefined,
    failure_reason: props.failureReason,
  })
}

export function trackCameraCaptureFailed(
  props: CameraCaptureDiagnosticsBase & {
    takeId?: string
    captureMode?: string
    failureStage: string
    failureReason: string
  },
): void {
  if (!initialized) return
  posthog.capture('camera_capture_failed', {
    ...cameraCaptureDiagnosticsProperties(props),
    take_id: props.takeId ?? undefined,
    capture_mode: props.captureMode ?? undefined,
    failure_stage: props.failureStage,
    failure_reason: props.failureReason,
  })
}

// ============================================================================
// First Generation Modal Events
// ============================================================================

export function trackProWelcomePageViewed(props: { isNewUser: boolean }): void {
  if (!initialized) return
  posthog.capture('pro_welcome_page_viewed', {
    is_new_user: props.isNewUser,
  })
}

export function trackFirstGenerationModalViewed(): void {
  if (!initialized) return
  posthog.capture('first_generation_modal_viewed')
}

export function trackFirstGenerationModalDismissed(): void {
  if (!initialized) return
  posthog.capture('first_generation_modal_dismissed')
}

export function trackFirstGenerationCompletedViewed(): void {
  if (!initialized) return
  posthog.capture('first_generation_completed_viewed')
}

export function trackFirstGenerationTimelineOpened(): void {
  if (!initialized) return
  posthog.capture('first_generation_timeline_opened')
}

// ============================================================================
// Crop Events (contract C24 — docs/plans/onboarding-v2-contracts.md)
// ============================================================================

export type CropCreatedSource = 'context_menu' | 'grid_preset'

/** A new crop child was created ("Crop Image…" or an internal grid preset). */
export function trackCropCreated(props: { source: CropCreatedSource }): void {
  if (!initialized) return
  posthog.capture('crop_created', {
    source: props.source,
  })
}

/** The Crop/Adjust tool opened. `session_id` joins every event from this opening. */
export function trackCropAdjustOpened(props: {
  sessionId: string
  entry: 'context_menu' | 'inspector' | 'mode_rail'
  isReedit: boolean
  operation: string | undefined
  renderer: 'webgl' | 'webgpu' | null
}): void {
  if (!initialized) return
  try {
    posthog.capture('crop_adjust_opened', {
      session_id: props.sessionId,
      entry: props.entry,
      is_reedit: props.isReedit,
      operation: props.operation ?? undefined,
      renderer: props.renderer ?? undefined,
    })
  } catch {
    // Telemetry never interrupts editing.
  }
}

/** One committed change to a Crop/Adjust control: which, how, from/to, and how long the gesture took. */
export function trackCropAdjustControlChanged(props: {
  sessionId: string
  control: string
  method: 'slider' | 'keyboard' | 'typed' | 'scrubbed' | 'stepped' | 'reset'
  from: number
  to: number
  activeMs: number
}): void {
  if (!initialized) return
  try {
    posthog.capture('crop_adjust_control_changed', {
      session_id: props.sessionId,
      control: props.control,
      method: props.method,
      from_value: props.from,
      to_value: props.to,
      active_ms: Math.round(props.activeMs),
    })
  } catch {
    // Telemetry never interrupts editing.
  }
}

/** The Crop/Adjust tool closed: outcome plus a usage summary of the whole session. */
export function trackCropAdjustClosed(
  sessionId: string,
  summary: {
    outcome: 'saved_copy' | 'saved_update' | 'cancelled'
    durationMs: number
    timeToFirstEditMs: number | null
    controlsTouched: string[]
    controlChanges: Record<string, number>
    controlActiveMs: Record<string, number>
    methodCounts: Record<string, number>
    interactionCounts: Record<string, number>
    stageActiveMs: number
    aspectChanges: number
    aspectFinal: string
    orientationSwaps: number
    compareSplit: number
    compareBefore: number
    peekHolds: number
    clippingToggles: number
    sectionResets: number
    resetAll: number
    saveAttempts: number
    saveFailures: number
  },
): void {
  if (!initialized) return
  try {
    posthog.capture('crop_adjust_closed', {
      session_id: sessionId,
      outcome: summary.outcome,
      duration_ms: summary.durationMs,
      time_to_first_edit_ms: summary.timeToFirstEditMs ?? undefined,
      controls_touched: summary.controlsTouched,
      controls_touched_count: summary.controlsTouched.length,
      control_changes: summary.controlChanges,
      control_active_ms: summary.controlActiveMs,
      method_counts: summary.methodCounts,
      interaction_counts: summary.interactionCounts,
      stage_active_ms: summary.stageActiveMs,
      aspect_changes: summary.aspectChanges,
      aspect_final: summary.aspectFinal,
      orientation_swaps: summary.orientationSwaps,
      compare_split: summary.compareSplit,
      compare_before: summary.compareBefore,
      peek_holds: summary.peekHolds,
      clipping_toggles: summary.clippingToggles,
      section_resets: summary.sectionResets,
      reset_all: summary.resetAll,
      save_attempts: summary.saveAttempts,
      save_failures: summary.saveFailures,
    })
  } catch {
    // Telemetry never interrupts closing.
  }
}

/** A Crop/Adjust save failed. `status` is the HTTP status when the backend answered (409 = edited elsewhere). */
export function trackCropAdjustSaveFailed(props: {
  sessionId: string
  mode: 'copy' | 'update'
  status: number | null
}): void {
  if (!initialized) return
  try {
    posthog.capture('crop_adjust_save_failed', {
      session_id: props.sessionId,
      mode: props.mode,
      status: props.status ?? undefined,
    })
  } catch {
    // Telemetry never interrupts editing.
  }
}

/** A crop/adjust save completed; counts non-neutral controls, excluding the grain seed. */
export function trackCropAdjustSaved(props: {
  sessionId: string
  mode: 'copy' | 'update'
  hasAngle: boolean
  adjustedFields: number
  usedSplit: boolean
}): void {
  if (!initialized) return
  try {
    posthog.capture('crop_adjust_saved', {
      session_id: props.sessionId,
      mode: props.mode,
      has_angle: props.hasAngle,
      adjusted_fields: props.adjustedFields,
      used_split: props.usedSplit,
    })
  } catch {
    // Telemetry must never interrupt a completed save or keep the modal open.
  }
}

// ============================================================================
// Image Editor Events
// ============================================================================

export type ImageToolOpenSource = 'context_menu' | 'mode_rail' | 'inspector'
export type ImageAnnotateSaveMode = 'create' | 'update' | 'save_as_new'
export type ImageAnnotateFailureStage = 'source_load' | 'annotation_load' | 'export' | 'save'
export type ImageEditSurface = 'unified_edit' | 'image_model'
export type ImageEditSubmitMode = 'masked_region' | 'whole_frame'
export type ImageEditFailureStage = 'source_load' | 'fetch_models' | 'sam2_segment' | 'mask_export' | 'mask_upload'

interface ImageEditorEventBase {
  projectId?: string
  sceneId?: string
  sourceAssetId?: string
  imageWidth?: number
  imageHeight?: number
}

export function trackImageAnnotateOpened(
  props: ImageEditorEventBase & { source: ImageToolOpenSource; isReedit: boolean },
): void {
  if (!initialized) return
  posthog.capture('image_annotate_opened', {
    project_id: props.projectId ?? undefined,
    scene_id: props.sceneId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    source: props.source,
    is_reedit: props.isReedit,
    image_width: props.imageWidth ?? undefined,
    image_height: props.imageHeight ?? undefined,
  })
}

export function trackImageAnnotateSaved(
  props: ImageEditorEventBase & {
    mode: ImageAnnotateSaveMode
    outputAssetId?: string
    hasStrokes: boolean
    textCount: number
  },
): void {
  if (!initialized) return
  posthog.capture('image_annotate_saved', {
    project_id: props.projectId ?? undefined,
    scene_id: props.sceneId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    output_asset_id: props.outputAssetId ?? undefined,
    mode: props.mode,
    has_strokes: props.hasStrokes,
    text_count: props.textCount,
    image_width: props.imageWidth ?? undefined,
    image_height: props.imageHeight ?? undefined,
  })
}

export function trackImageAnnotateFailed(props: ImageEditorEventBase & { stage: ImageAnnotateFailureStage }): void {
  if (!initialized) return
  posthog.capture('image_annotate_failed', {
    project_id: props.projectId ?? undefined,
    scene_id: props.sceneId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    stage: props.stage,
    image_width: props.imageWidth ?? undefined,
    image_height: props.imageHeight ?? undefined,
  })
}

export function trackImageEditOpened(
  props: ImageEditorEventBase & { source: ImageToolOpenSource; surface: ImageEditSurface; supportsMask: boolean },
): void {
  if (!initialized) return
  posthog.capture('image_edit_opened', {
    project_id: props.projectId ?? undefined,
    scene_id: props.sceneId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    source: props.source,
    surface: props.surface,
    supports_mask: props.supportsMask,
    image_width: props.imageWidth ?? undefined,
    image_height: props.imageHeight ?? undefined,
  })
}

export function trackImageEditSubmitted(
  props: ImageEditorEventBase & {
    mode: ImageEditSubmitMode
    modelId?: string
    promptLength: number
    inverted?: boolean
  },
): void {
  if (!initialized) return
  posthog.capture('image_edit_submitted', {
    project_id: props.projectId ?? undefined,
    scene_id: props.sceneId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    mode: props.mode,
    model_id: props.modelId ?? undefined,
    prompt_length: props.promptLength,
    inverted: props.inverted ?? undefined,
    image_width: props.imageWidth ?? undefined,
    image_height: props.imageHeight ?? undefined,
  })
}

export function trackImageEditFailed(props: ImageEditorEventBase & { stage: ImageEditFailureStage }): void {
  if (!initialized) return
  posthog.capture('image_edit_failed', {
    project_id: props.projectId ?? undefined,
    scene_id: props.sceneId ?? undefined,
    source_asset_id: props.sourceAssetId ?? undefined,
    stage: props.stage,
    image_width: props.imageWidth ?? undefined,
    image_height: props.imageHeight ?? undefined,
  })
}

// ============================================================================
// Onboarding V2 Events (contract C4 — docs/plans/onboarding-v2-contracts.md)
// The wave-0 freeze is lifted; additions follow the C4 naming conventions.
// v3 additions follow contract C24.
// ============================================================================

export type OnboardingStep = 's2_project_setup' | 's1_brief' | 's1_sample' | 's3_survey'
export type OnboardingSurveyQuestion = 'workplace' | 'team_size' | 'ai_experience' | 'referral_channel'

export type FalPlan = 'free' | 'pro' | 'studio'
/** The fal setup chooser's paths: the agent walk-through, the app's key and prompt, the normal onboarding. */
export type FalOnboardingStartPath = 'agent' | 'app' | 'explore'
/** How the person left the fal setup: by a path, or "Skip for now". */
export type FalOnboardingConnectPath = FalOnboardingStartPath | 'skipped'
/** The page the fal-key plan button was on (`via=` on `/welcome?intent=fal`), when it said. */
export type FalOnboardingVia = 'bar' | 'pricing'

/** fal setup (`/welcome?intent=fal`), Studio only: the team org was created. */
export function trackFalOnboardingTeamCreated(props: { teamId: string; role: string; teamSize: string }): void {
  if (!initialized) return
  posthog.capture('fal_onboarding_team_created', {
    team_id: props.teamId,
    role: props.role,
    team_size: props.teamSize,
    $groups: { team: props.teamId },
  })
}

/** fal setup finished: the user left the flow by one of the chooser's paths, or skipped it. */
export function trackFalOnboardingFinished(props: {
  plan: FalPlan
  orgId: string
  invitesSent: number
  connectPath: FalOnboardingConnectPath
  via?: FalOnboardingVia
}): void {
  if (!initialized) return
  posthog.capture('fal_onboarding_finished', {
    plan: props.plan,
    org_id: props.orgId,
    invites_sent: props.invitesSent,
    connect_path: props.connectPath,
    via: props.via,
    ...(props.plan === 'studio' ? { $groups: { team: props.orgId } } : {}),
  })
}

/**
 * fal setup's chooser: the person picked where to start. Also kept on the person as `fal_onboarding_path`, so later
 * events can be broken down by it.
 */
export function trackFalOnboardingPathSelected(props: {
  plan: FalPlan
  orgId: string
  path: FalOnboardingStartPath
  via?: FalOnboardingVia
}): void {
  if (!initialized) return
  posthog.capture('fal_onboarding_path_selected', {
    plan: props.plan,
    org_id: props.orgId,
    path: props.path,
    via: props.via,
    ...(props.plan === 'studio' ? { $groups: { team: props.orgId } } : {}),
  })
  posthog.setPersonProperties({ fal_onboarding_path: props.path })
}

/**
 * fal setup's "Connect your app" screens (`components/onboarding/fal/ConnectAppFlow.tsx`): one event per beat,
 * `fal_connect_<beat>`, every one keyed by the plan session the screens share (`plan_session_id`), so a session's
 * beats line up with its agent's check-ins (server-side `fal_plan_checkin_received`) and its row in Backstage.
 * `screen` is `chooser`, `plan` or `app_setup`. The person's own line under "What stopped you?" stays out of
 * PostHog: it lives in the session's event log only.
 */
export function trackFalConnectBeat(
  beat: FalConnectBeat,
  props: {
    planSessionId: string | null
    orgId: string
    screen: FalConnectScreen
    /** `app_setup`: the first job the prompt names. */
    job?: string
    /** `progress_seen`: the milestone the page showed. */
    step?: string
    /** `done`: the path the person went on to. */
    destination?: string
    /** `leave_reason`: the answer. */
    reason?: FalConnectLeaveReason
    /** `prompt_copied`: a copy after the agent had connected. */
    again?: boolean
    /** The org's id when it is a team (Studio): the beat rolls up under the team. */
    teamId?: string | null
  },
): void {
  if (!initialized) return
  posthog.capture(`fal_connect_${beat}`, {
    plan_session_id: props.planSessionId ?? undefined,
    org_id: props.orgId,
    screen: props.screen,
    job: props.job,
    step: props.step,
    destination: props.destination,
    reason: props.reason,
    again: props.again,
    ...(props.teamId ? { $groups: { team: props.teamId } } : {}),
  })
}

/**
 * The fal key offer on a project (`components/billing/FalKeyOfferDialog.tsx`, and its row in the canvas "Getting
 * started" checklist): shown when the dialog opens by itself over the canvas, opened when the checklist row opens
 * it, saved when a key is saved through it, and dismissed when it closes without one (for good, as far as the
 * dialog goes). `source` says which of the two surfaces it was; `teamId`, when the org is a team, rolls the event
 * up under it.
 */
export type FalKeyOfferSource = 'modal' | 'checklist'

export function trackFalKeyOffer(
  action: 'shown' | 'opened' | 'dismissed' | 'saved',
  props: { orgId: string; source: FalKeyOfferSource; teamId?: string | null },
): void {
  if (!initialized) return
  posthog.capture(`fal_key_offer_${action}`, {
    org_id: props.orgId,
    source: props.source,
    ...(props.teamId ? { $groups: { team: props.teamId } } : {}),
  })
}

/**
 * The connect screens are recorded whenever PostHog may record at all: the sampling rate is overridden for this
 * session, consent regions still wait for consent. Returns the replay's session id once a recording runs, so a
 * beat can name it; null when nothing is recording (replay off in the project, consent withheld, recorder not
 * loaded yet).
 */
export function startFalConnectReplay(): string | null {
  if (!initialized) return null
  if (consentRequired() && readConsentChoice() !== 'granted') return null
  try {
    posthog.startSessionRecording({ sampling: true })
  } catch {
    return null
  }
  return activeReplaySessionId()
}

/** The PostHog session id, when a replay is being recorded right now; null otherwise. */
export function activeReplaySessionId(): string | null {
  if (!initialized) return null
  try {
    return posthog.sessionRecordingStarted() ? posthog.get_session_id() : null
  } catch {
    return null
  }
}

/**
 * What the trial step's subscribe call led to: `stripe` = sent to the Stripe page, `saved_card` = the plan
 * started on the card already on file, `scheduled` = a plan switch waits for the current period to end,
 * `already_subscribed` = the org already held the plan.
 */
export type FalTrialCheckout = 'stripe' | 'saved_card' | 'scheduled' | 'already_subscribed'

/**
 * fal setup, Pro and Studio: the trial step's subscribe call went through. Sent as a beacon because the
 * `stripe` path leaves the page right after.
 */
export function trackFalTrialCheckoutStarted(props: {
  plan: FalPlan
  orgId: string
  checkout: FalTrialCheckout
  trialAvailable?: boolean
}): void {
  if (!initialized) return
  posthog.capture(
    'fal_trial_checkout_started',
    {
      plan: props.plan,
      org_id: props.orgId,
      checkout: props.checkout,
      trial_available: props.trialAvailable ?? undefined,
      ...(props.plan === 'studio' ? { $groups: { team: props.orgId } } : {}),
    },
    { transport: 'sendBeacon' },
  )
}

/**
 * fal setup, Pro and Studio: the trial step's subscribe call didn't start the plan. `error` = the request failed,
 * `unsuccessful` = it answered with neither a checkout nor a started plan.
 */
export function trackFalTrialCheckoutFailed(props: {
  plan: FalPlan
  orgId: string
  period: 'monthly' | 'annual'
  reason: 'error' | 'unsuccessful'
}): void {
  if (!initialized) return
  posthog.capture('fal_trial_checkout_failed', {
    plan: props.plan,
    org_id: props.orgId,
    period: props.period,
    reason: props.reason,
    ...(props.plan === 'studio' ? { $groups: { team: props.orgId } } : {}),
  })
}

/**
 * What Continue on a fal-key card of `/select-plan` did: `fal_setup` = opened fal setup, `plan_switch` = opened the
 * switch confirmation (a live paid Pro), `current_plan` = the plan already held, `contact_sales` = Enterprise's form.
 */
export type FalPlanSelectedAction = 'fal_setup' | 'plan_switch' | 'current_plan' | 'contact_sales'

/** `/select-plan`, own fal key view: Continue with a fal-key plan selected (the olive cards send `plan_selected`). */
export function trackFalPlanSelected(props: {
  plan: FalPlan | 'enterprise'
  period: 'monthly' | 'annual'
  action: FalPlanSelectedAction
  source: string
}): void {
  if (!initialized) return
  posthog.capture('fal_plan_selected', {
    plan: props.plan,
    period: props.period,
    action: props.action,
    source: props.source,
  })
}

/** fal setup: back from the Stripe page, the checkout was confirmed (or the confirmation failed). */
export function trackFalTrialCheckoutReturned(props: {
  plan: FalPlan
  orgId?: string
  outcome: 'confirmed' | 'error'
  status?: string
}): void {
  if (!initialized) return
  posthog.capture('fal_trial_checkout_returned', {
    plan: props.plan,
    org_id: props.orgId ?? undefined,
    outcome: props.outcome,
    status: props.status ?? undefined,
    ...(props.plan === 'studio' && props.orgId ? { $groups: { team: props.orgId } } : {}),
  })
}

export function trackOnboardingStepViewed(props: { step: OnboardingStep }): void {
  if (!initialized) return
  posthog.capture('onboarding_step_viewed', {
    step: props.step,
  })
}

export function trackOnboardingProjectSetupCompleted(props: { visibility: 'public' | 'private' }): void {
  if (!initialized) return
  posthog.capture('onboarding_project_setup_completed', {
    visibility: props.visibility,
  })
}

export function trackOnboardingBriefSubmitted(props: {
  intent: OnboardingIntent
  briefEdited: boolean
  briefLength: number
}): void {
  if (!initialized) return
  posthog.capture('onboarding_brief_submitted', {
    intent: props.intent,
    brief_edited: props.briefEdited,
    brief_length: props.briefLength,
  })
}

export function trackOnboardingSampleSelected(props: { sampleId: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_sample_selected', {
    sample_id: props.sampleId,
  })
}

/** S1 "Start with a blank canvas instead" — exits the stepper to an empty canvas without drafting. */
export function trackOnboardingSkippedToBlank(props?: { source?: 'motion_v2_image_step' }): void {
  if (!initialized) return
  posthog.capture('onboarding_skipped_to_blank', { source: props?.source })
}

export function trackOnboardingQuestionAnswered(props: { question: OnboardingSurveyQuestion; answer: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_question_answered', {
    question: props.question,
    answer: props.answer,
  })
}

export function trackOnboardingSurveyQuestionViewed(props: { question: OnboardingSurveyQuestion }): void {
  if (!initialized) return
  posthog.capture('onboarding_survey_question_viewed', {
    question: props.question,
  })
}

/**
 * Mirrors the survey answers onto the PostHog person ($set) using the SAME
 * property names the backend survey handler writes (backend/src/onboarding/survey.ts
 * buildOnboardingPersonProperties → onboarding_workplace / onboarding_team_size /
 * onboarding_ai_experience). The classic flow relies on the backend mirror alone,
 * but the motion render-room survey writes these client-side too so downstream
 * `onboarding_motion_*` events are immediately cohort-breakable by survey answer
 * (person-on-events) without waiting on the server round-trip. Only answered keys
 * are set — a partial answer must never blank out an earlier one.
 */
export function setOnboardingSurveyPersonProperties(answers: {
  workplace?: string
  team_size?: string
  ai_experience?: string
}): void {
  if (!initialized) return
  const properties: Record<string, string> = {}
  if (answers.workplace) properties.onboarding_workplace = answers.workplace
  if (answers.team_size) properties.onboarding_team_size = answers.team_size
  if (answers.ai_experience) properties.onboarding_ai_experience = answers.ai_experience
  if (Object.keys(properties).length === 0) return
  posthog.setPersonProperties(properties)
}

export function trackMobileBlocked(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('mobile_blocked', {
    project_id: props.projectId,
  })
}

export function trackHandoffEmailRequested(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('handoff_email_requested', {
    project_id: props.projectId,
  })
}

export function trackCameraRollUploadStarted(props: { fileCount: number }): void {
  if (!initialized) return
  posthog.capture('camera_roll_upload_started', {
    file_count: props.fileCount,
  })
}

export function trackCameraRollUploadCompleted(props: { fileCount: number }): void {
  if (!initialized) return
  posthog.capture('camera_roll_upload_completed', {
    file_count: props.fileCount,
  })
}

export function trackNarrowViewportBannerShown(): void {
  if (!initialized) return
  posthog.capture('narrow_viewport_banner_shown')
}

/** Per-device-once cohort event for the Stage-2 default flip (gate via consumeOnboardingV2DefaultAppliedFlag). */
export function trackOnboardingV2DefaultApplied(): void {
  if (!initialized) return
  posthog.capture('onboarding_v2_default_applied')
}

/**
 * v3 (contract C24): the user clicked Generate on the house-funded animatic
 * (the activation moment). Fired on click, before the POST resolves —
 * outcome events (onboarding_animatic_fired/_completed/_failed) are server-side.
 */
export function trackOnboardingAnimaticCtaClicked(): void {
  if (!initialized) return
  posthog.capture('onboarding_animatic_cta_clicked')
}

/**
 * The on-house animatic's generation panel became visible in the inspector.
 * `via: 'deep_link'` = auto-selected by the survey's Open Project hop;
 * `manual_select` = the user clicked the animatic node themselves.
 */
export function trackOnboardingAnimaticPanelViewed(props: { via: 'deep_link' | 'manual_select' }): void {
  if (!initialized) return
  posthog.capture('onboarding_animatic_panel_viewed', {
    via: props.via,
  })
}

// ============================================================================
// Onboarding Motion Events
// The second onboarding variant (camera-motion / "step into the set"). Every
// client event hardcodes `flow: 'onboarding_motion'` so the funnel is isolable
// from the v2/v3 storyboard onboarding. snake_case event names + props.
// ============================================================================

/** 'experience' is the front-door routing question, 'agent' the MCP walk-through. */
export type OnboardingMotionStep =
  'experience' | 'agent' | 'basics' | 'setup' | 'image' | 'set' | 'direction' | 'render_room'
/** `fal_explore`: "Just use Martini" from the fal setup's chooser, entering at the experience question. */
export type OnboardingMotionEntrySource = 'default' | 'motion_share' | 'set_handoff' | 'agent_handoff' | 'fal_explore'

/**
 * Which door/path the user is on at the front-door fork. The original motion
 * fork was 'guided' (step-into-a-set) | 'agent'; v3 adds the two Basics
 * variants. Additive union — existing 'guided'/'agent' dashboards are
 * unaffected; new rows just carry a new `path` value.
 */
export type OnboardingStartPath = 'guided' | 'agent' | 'basics_single' | 'basics_narrative'

/**
 * v2 front-door fork outcome. 'guided' = the step-into-set flow (also fired for
 * non-power-users, who never see the fork — so the event totals the whole
 * cohort and the split is readable from one event). 'agent' = straight to the
 * canvas MCP checklist after project setup. v3 additionally emits
 * 'basics_single' | 'basics_narrative' for the redesigned Basics doors.
 * `flow` stays 'onboarding_motion' for back-compat with the existing funnel.
 */
export function trackOnboardingStartPathSelected(props: {
  path: OnboardingStartPath
  experienceAnswer: ExperienceAnswer
}): void {
  if (!initialized) return
  posthog.capture('onboarding_start_path_selected', {
    flow: 'onboarding_motion',
    path: props.path,
    experience_answer: props.experienceAnswer,
  })
  // Person-level so canvas-side outcomes (MCP milestones, first generation,
  // checkout) are cohortable by the door taken without a funnel window.
  posthog.setPersonProperties({ onboarding_start_path: props.path })
}

/** Agent wizard: which client they picked (claude | claudeCode | chatgpt | cursor | more_dialog). */
export function trackOnboardingAgentClientPicked(props: { client: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_agent_client_picked', {
    flow: 'onboarding_motion',
    client: props.client,
  })
}

/** Agent wizard: the visibility Continue commit (public advanced | private -> checkout | private already-Pro). */
export function trackOnboardingAgentVisibilityCommitted(props: {
  visibility: 'public' | 'private'
  toCheckout: boolean
}): void {
  if (!initialized) return
  posthog.capture('onboarding_agent_visibility_committed', {
    flow: 'onboarding_motion',
    visibility: props.visibility,
    to_checkout: props.toCheckout,
  })
}

/** Agent wizard: the door into the live canvas after the agent's project appeared. */
export function trackOnboardingAgentWatchBuildClicked(props: { projectId: string; draftCount: number }): void {
  if (!initialized) return
  posthog.capture('onboarding_agent_watch_build_clicked', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    draft_count: props.draftCount,
  })
}

// ============================================================================
// Onboarding V3 Events (Basics + redesigned flow)
// A few helpers are still DARK, with no call site (see ANALYTICS.md). `flow` is
// the door/path that led here (OnboardingStartPath), shared across the
// canvas-arrival events.
// ============================================================================

/** Basics-flow steps that emit a view / pick / generate event. Single-shot:
 *  setup → image → video. Narrative: character → setting → scene (→ video).
 *  'setting' matches the generation API's BasicsImageKind vocabulary. */
export type OnboardingBasicsStep =
  | 'setup'
  | 'image'
  | 'video'
  | 'character'
  | 'setting'
  | 'scene'
  // The scene screen's inner beats (narrative variant): the combined still,
  // the video prompt, then the payoff -> discord -> olives run-out.
  | 'scene_image'
  | 'motion_prompt'
  | 'payoff'
  | 'discord'
  | 'olives'

/** The cast-and-set (or first-shot) reveal became visible on the canvas. */
export function trackOnboardingCanvasRevealViewed(props: { flow: OnboardingStartPath }): void {
  if (!initialized) return
  posthog.capture('onboarding_canvas_reveal_viewed', { flow: props.flow })
}

/** A Basics step became visible. */
export function trackOnboardingBasicsStepViewed(props: { step: OnboardingBasicsStep }): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_step_viewed', { step: props.step })
}

/** The user created a reusable element (a Subject, Set, variable, or reference). */
export function trackOnboardingBasicsElementCreated(props: {
  kind: 'character' | 'set' | 'variable' | 'reference'
}): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_element_created', { kind: props.kind })
}

/** The narrative variant composed its Subject + Set into a scene with @-mentions. */
export function trackOnboardingBasicsSceneComposed(): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_scene_composed')
}

/** The user picked one of the 4 generated variations at a step. */
export function trackOnboardingBasicsVariationPicked(props: { step: OnboardingBasicsStep }): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_variation_picked', { step: props.step })
}

/** The image-done branch choice: turn it into a video, or try camera control. */
export function trackOnboardingBasicsBringToLifeSelected(props: { choice: 'video' | 'camera_control' }): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_bring_to_life_selected', { choice: props.choice })
}

/** The user clicked Generate on a Basics step (activation moment; outcomes are server-side). */
export function trackOnboardingBasicsGenerateClicked(props: { step: OnboardingBasicsStep }): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_generate_clicked', { step: props.step })
}

/** How a Basics fire resolved ON SCREEN — the client-side truth the funnel
 * needs to split "gave up" from "we failed them". `partial` = a variation
 * batch where some tiles landed and the user could still pick; `capped` = the
 * daily house budget dead-end. */
export function trackOnboardingBasicsGenerationResult(props: {
  step: OnboardingBasicsStep
  outcome: 'completed' | 'partial' | 'failed' | 'capped'
}): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_generation_result', { step: props.step, outcome: props.outcome })
}

/** Terminal funnel marker: the Basics flow finished and the canvas hand-off is
 * about to navigate. `canvas_filled` false = the degraded arrival (project
 * created but the doc could not be seeded, so no tour). */
export function trackOnboardingBasicsCompleted(props: { projectId: string; canvasFilled: boolean }): void {
  if (!initialized) return
  posthog.capture('onboarding_basics_completed', {
    project_id: props.projectId,
    canvas_filled: props.canvasFilled,
  })
}

/** The agent path's project appeared and its first drafts started landing on the canvas. */
export function trackOnboardingAgentDraftStarted(props: { projectId: string; draftCount: number }): void {
  if (!initialized) return
  posthog.capture('onboarding_agent_draft_started', {
    project_id: props.projectId,
    draft_count: props.draftCount,
  })
}

/** A mobile user hit the desktop-only ceiling for their path (canvas handoff). */
export function trackOnboardingMobileCeilingReached(props: { path: OnboardingStartPath; atStep: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_mobile_ceiling_reached', {
    path: props.path,
    at_step: props.atStep,
  })
}

/** The user skipped a render-room survey question instead of answering it. */
export function trackOnboardingRenderSurveySkipped(props: {
  question: OnboardingSurveyQuestion
  flow: OnboardingStartPath
}): void {
  if (!initialized) return
  posthog.capture('onboarding_render_survey_skipped', {
    question: props.question,
    flow: props.flow,
  })
}

/**
 * The front-door fork became visible. Replaced the two-door fork's
 * `onboarding_agent_door_viewed`; `doors` is the set of door ids offered
 * (routing depends on the experience answer — first-timers don't see the
 * camera-control door — and the fal entry drops the agent door).
 */
export function trackOnboardingDoorViewed(props: { experienceAnswer: ExperienceAnswer; doors: string[] }): void {
  if (!initialized) return
  posthog.capture('onboarding_door_viewed', {
    experience_answer: props.experienceAnswer,
    doors: props.doors,
  })
}

// ============================================================================
// Blank-canvas example deck (the activation layer on the empty state)
// ============================================================================

/** A runnable recipe card was clicked (bundle lands on the canvas). */
export function trackExampleRecipeClicked(props: { recipeId: string; aiExperience?: string | null }): void {
  if (!initialized) return
  posthog.capture('canvas_example_recipe_clicked', {
    recipe_id: props.recipeId,
    ai_experience: props.aiExperience ?? undefined,
  })
}

/** An agent-variant prompt card was copied. */
export function trackExamplePromptCopied(props: { promptId: string }): void {
  if (!initialized) return
  posthog.capture('canvas_example_prompt_copied', { prompt_id: props.promptId })
}

/** The Learn Martini sibling was opened from the deck. */
export function trackExampleProjectOpened(): void {
  if (!initialized) return
  posthog.capture('canvas_example_project_opened')
}

/** The motion onboarding's welcome frame became visible. `isHandheld` = the handheld/default-motion variant. */
export function trackOnboardingMotionWelcomeViewed(props: {
  isHandheld: boolean
  entrySource?: OnboardingMotionEntrySource
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_welcome_viewed', {
    flow: 'onboarding_motion',
    is_handheld: props.isHandheld,
    entry_source: props.entrySource ?? 'default',
  })
}

/** Per-device-once cohort event for the motion-onboarding default flip (gate via consumeOnboardingMotionDefaultAppliedFlag). */
export function trackOnboardingMotionDefaultApplied(): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_default_applied', {
    flow: 'onboarding_motion',
  })
}

/** A motion-onboarding step became visible. */
export function trackOnboardingMotionStepViewed(props: { step: OnboardingMotionStep; projectId?: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_step_viewed', {
    flow: 'onboarding_motion',
    step: props.step,
    project_id: props.projectId,
  })
}

/** The user chose the image they'll animate (upload, sample, or freshly generated). */
export type OnboardingMotionPresetId = 'stopmotion-couple' | 'garden-friends' | 'portrait-closeup'

type OnboardingMotionImageSelectedProps =
  | { source: 'sample'; projectId: string; presetId: OnboardingMotionPresetId; entry?: 'recovery' }
  | { source: 'upload' | 'generated'; projectId: string; presetId?: never; entry?: never }

export function trackOnboardingMotionImageSelected(props: OnboardingMotionImageSelectedProps): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_image_selected', {
    flow: 'onboarding_motion',
    source: props.source,
    project_id: props.projectId,
    preset_id: props.presetId,
    entry: props.entry,
  })
}

/** The selected image's navigable scene reached a terminal preparation outcome. */
export function trackOnboardingMotionSceneReady(props: {
  projectId: string
  source: 'upload' | 'sample'
  durationMs: number
  outcome: 'ready' | 'timed_out' | 'failed' | 'blocked' | 'upload_timed_out'
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_scene_ready', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    source: props.source,
    duration_ms: props.durationMs,
    outcome: props.outcome,
  })
}

/** The user engaged the fly-through / camera-motion editor (first interaction). */
export function trackOnboardingMotionFlyEngaged(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_fly_engaged', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
  })
}

/** The user clicked the render CTA (activation moment); outcomes are server-side `onboarding_motion_render_*`. */
export function trackOnboardingMotionRenderCtaClicked(props: { hasDirectionNote: boolean; projectId: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_render_cta_clicked', {
    flow: 'onboarding_motion',
    has_direction_note: props.hasDirectionNote,
    project_id: props.projectId,
  })
}

/** The reveal (final result) became visible. `renderDone` = whether the render had finished by reveal time. */
export function trackOnboardingMotionRevealViewed(props: {
  projectId: string
  subjectCount: number
  renderDone: boolean
  libraryPath: boolean
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_reveal_viewed', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    subject_count: props.subjectCount,
    render_done: props.renderDone,
    library_path: props.libraryPath,
  })
}

/** The user dismissed the reveal. `renderDone` = whether the render had finished when dismissed. */
export function trackOnboardingMotionRevealDismissed(props: {
  projectId: string
  renderDone: boolean
  libraryPath: boolean
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_reveal_dismissed', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    render_done: props.renderDone,
    library_path: props.libraryPath,
  })
}

export type OnboardingCanvasTourStep = 'welcome' | 'toolbar' | 'timeline' | 'navigate'

/** Which arrival the first-open canvas tour served: the camera-motion
 * hand-off or the Basics guided path. */
export type OnboardingCanvasTourFlow = 'onboarding_motion' | 'basics_narrative'

export function trackOnboardingCanvasTourViewed(props: { projectId: string; flow: OnboardingCanvasTourFlow }): void {
  if (!initialized) return
  posthog.capture('onboarding_canvas_tour_viewed', {
    flow: props.flow,
    project_id: props.projectId,
  })
}

export function trackOnboardingCanvasTourStepViewed(props: {
  projectId: string
  flow: OnboardingCanvasTourFlow
  step: OnboardingCanvasTourStep
}): void {
  if (!initialized) return
  posthog.capture('onboarding_canvas_tour_step_viewed', {
    flow: props.flow,
    project_id: props.projectId,
    step: props.step,
  })
}

export function trackOnboardingCanvasTourCompleted(props: { projectId: string; flow: OnboardingCanvasTourFlow }): void {
  if (!initialized) return
  posthog.capture('onboarding_canvas_tour_completed', {
    flow: props.flow,
    project_id: props.projectId,
  })
}

export function trackOnboardingCanvasTourSkipped(props: {
  projectId: string
  flow: OnboardingCanvasTourFlow
  step: OnboardingCanvasTourStep
}): void {
  if (!initialized) return
  posthog.capture('onboarding_canvas_tour_skipped', {
    flow: props.flow,
    project_id: props.projectId,
    step: props.step,
  })
}

export function trackOnboardingCanvasTourGestureDone(props: {
  projectId: string
  flow: OnboardingCanvasTourFlow
  gesture: 'pan' | 'pan_x' | 'zoom'
  input: 'trackpad' | 'mouse'
}): void {
  if (!initialized) return
  posthog.capture('onboarding_canvas_tour_gesture_done', {
    flow: props.flow,
    project_id: props.projectId,
    gesture: props.gesture,
    input: props.input,
  })
}

type OnboardingMotionRenderStatus = 'rendering' | 'rendered' | 'failed' | 'unknown'

export function trackOnboardingMotionPreparedShotCtaViewed(props: {
  projectId: string
  assetId: string
  renderStatus: OnboardingMotionRenderStatus
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_prepared_shot_cta_viewed', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    asset_id: props.assetId,
    render_status: props.renderStatus,
  })
}

export function trackOnboardingMotionPreparedShotOpened(props: {
  projectId: string
  assetId: string
  renderStatus: OnboardingMotionRenderStatus
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_prepared_shot_opened', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    asset_id: props.assetId,
    render_status: props.renderStatus,
  })
}

export function trackOnboardingMotionResultReadyPromptViewed(props: { projectId: string; assetId: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_result_ready_prompt_viewed', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    asset_id: props.assetId,
  })
}

export function trackOnboardingMotionRenderRecoveryPromptViewed(props: { projectId: string; assetId?: string }): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_render_recovery_prompt_viewed', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    asset_id: props.assetId,
  })
}

export function trackOnboardingMotionRenderRecoveryClicked(props: {
  projectId: string
  assetId?: string
  action: 'choose_different_image' | 'dismiss'
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_render_recovery_clicked', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    asset_id: props.assetId,
    action: props.action,
  })
}

/**
 * The real failure surface. The `_render_recovery_prompt_*` events fire on
 * every render (they are the render-room continue prompt), so they cannot
 * measure failures — this fires once per failed shot, per surface.
 */
export function trackOnboardingMotionRenderFailureViewed(props: {
  projectId: string
  assetId?: string
  reason: string
  surface: 'reveal' | 'image_step' | 'set_step'
}): void {
  if (!initialized) return
  posthog.capture('onboarding_motion_render_failure_viewed', {
    flow: 'onboarding_motion',
    project_id: props.projectId,
    asset_id: props.assetId,
    reason: props.reason,
    surface: props.surface,
  })
}

/** The emailed send-to-desktop link was opened — on which kind of device, and what it did. */
export function trackHandoffLinkOpened(props: {
  projectId: string
  deviceType: 'desktop' | 'mobile'
  variant: 'signed_in' | 'still_on_phone'
}): void {
  if (!initialized) return
  posthog.capture('handoff_link_opened', {
    project_id: props.projectId,
    device_type: props.deviceType,
    variant: props.variant,
  })
}

/** Blank-canvas example deck rendered (impression) — pairs with `canvas_example_recipe_clicked`. */
export function trackCanvasExampleDeckViewed(): void {
  if (!initialized) return
  posthog.capture('canvas_example_deck_viewed')
}

// ============================================================================
// Session Idle Tracking (for Slack recording notifications via data pipeline)
// ============================================================================

const IDLE_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Fires a `session_idle_five_minutes` event when the user is idle for 5 minutes.
 * The event includes the session recording URL so PostHog's Slack data pipeline
 * destination can post a direct link to the recording.
 *
 * Call once after PostHog is initialized. Cleans up on page unload.
 */
export function setupSessionIdleTracking(): () => void {
  if (!initialized || typeof window === 'undefined') return () => {}

  let timer: ReturnType<typeof setTimeout> | null = null
  let hasFired = false

  function resetTimer() {
    if (hasFired) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(onIdle, IDLE_TIMEOUT_MS)
  }

  function onIdle() {
    if (hasFired) return
    hasFired = true

    const replayUrl = posthog.get_session_replay_url({ withTimestamp: true })

    posthog.capture('session_idle_five_minutes', {
      $session_replay_url: replayUrl,
    })
  }

  const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const
  const listenerOptions: AddEventListenerOptions = { passive: true }
  for (const event of events) {
    window.addEventListener(event, resetTimer, listenerOptions)
  }

  // Start the initial timer
  resetTimer()

  // Return cleanup function
  return () => {
    if (timer) clearTimeout(timer)
    for (const event of events) {
      window.removeEventListener(event, resetTimer, listenerOptions)
    }
  }
}

// ============================================================================
// Canvas V3 Multiplayer Events
// ============================================================================

/**
 * Fired when the multiplayer provider reports a disconnect. Captured on
 * reconnect with the total disconnect duration so we can understand
 * offline-edit frequency.
 *
 * `closeCode` / `closeReason` carry the WebSocket close frame that started the
 * outage, which is the only thing that separates causes that look identical
 * once the socket is back: a server verdict (1008 'Project access
 * downgraded'), a server lifecycle close (1013 'migrating'), a transport drop
 * with no close frame at all (1006), or a worker redeploy (1012). Duration
 * alone cannot tell those apart, and each has a different fix.
 */
export function trackCanvasMultiplayerDisconnect(props: {
  durationMs: number
  pendingChanges: number
  panelVersion: 'v3'
  projectId: string
  closeCode?: number
  closeReason?: string
}): void {
  if (!initialized) return
  posthog.capture('canvas_multiplayer_disconnect', {
    duration_ms: props.durationMs,
    pending_changes: props.pendingChanges,
    panel_version: props.panelVersion,
    project_id: props.projectId,
    close_code: props.closeCode,
    close_reason: props.closeReason,
  })
}

/**
 * Fired once per canvas multiplayer session (when the canvas panel
 * unmounts). Captures aggregate usage so we can see which features
 * drive engagement.
 */
export function trackCanvasMultiplayerSession(props: {
  concurrentUsersMax: number
  durationMs: number
  cursorMovesPerSecAvg: number
  selectionBroadcasts: number
  projectId: string
}): void {
  if (!initialized) return
  posthog.capture('canvas_multiplayer_session', {
    concurrent_users_max: props.concurrentUsersMax,
    duration_ms: props.durationMs,
    cursor_moves_per_sec_avg: props.cursorMovesPerSecAvg,
    selection_broadcasts: props.selectionBroadcasts,
    project_id: props.projectId,
  })
}

// ============================================================================
// MCP Authorization
// ============================================================================

export function trackMcpAuthorizationCompleted(props: { clientType: string }): void {
  if (!initialized) return
  posthog.capture('mcp_authorization_completed', { client_type: props.clientType }, { transport: 'sendBeacon' })
}

export function trackMcpAuthorizationDeclined(props: { clientType: string }): void {
  if (!initialized) return
  posthog.capture('mcp_authorization_declined', { client_type: props.clientType }, { transport: 'sendBeacon' })
}

export function trackMcpAuthorizationBlocked(props: {
  clientType: string
  redirectDomain?: string | null
  clientName?: string | null
}): void {
  if (!initialized) return
  posthog.capture(
    'mcp_authorization_blocked',
    {
      client_type: props.clientType,
      redirect_domain: props.redirectDomain ?? undefined,
      client_name: props.clientName ?? undefined,
    },
    { transport: 'sendBeacon' },
  )
}

export function trackMcpAuthorizationFailed(props: { reason: string; clientType?: string | null }): void {
  if (!initialized) return
  posthog.capture(
    'mcp_authorization_failed',
    {
      reason: props.reason,
      client_type: props.clientType ?? 'unknown',
    },
    { transport: 'sendBeacon' },
  )
}

// ============================================================================
// MCP Onboarding Checklist (the "Getting started with MCP" canvas surface)
// ============================================================================

export type McpOnboardingGuideSource = 'checklist' | 'animatic_modal'

/** The bottom-right checklist popover was opened. */
export function trackMcpOnboardingChecklistOpened(props: { completedCount: number; totalCount: number }): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_checklist_opened', {
    completed_count: props.completedCount,
    total_count: props.totalCount,
  })
}

/** The user hid the checklist (re-enabled from Preferences). */
export function trackMcpOnboardingChecklistHidden(): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_checklist_hidden')
}

/** The MCP overview video modal was opened from the checklist. */
export function trackMcpOnboardingVideoOpened(): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_video_opened')
}

/** The MCP setup guide was opened from an onboarding surface. */
export function trackMcpOnboardingGuideOpened(props: { source: McpOnboardingGuideSource }): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_guide_opened', { source: props.source })
}

// ============================================================================
// Learn assistant
// ============================================================================

export function trackLearnAssistantOpened(props: { pagePath: string; locale: string; source: 'header' }): void {
  if (!initialized) return
  posthog.capture('learn_assistant_opened', {
    flow: 'learn_assistant',
    page_path: props.pagePath,
    locale: props.locale,
    source: props.source,
  })
}

export function trackLearnAssistantQuestionAsked(props: {
  pagePath: string
  locale: string
  turnIndex: number
  question: string
  questionLength: number
}): void {
  if (!initialized) return
  posthog.capture('learn_assistant_question_asked', {
    flow: 'learn_assistant',
    page_path: props.pagePath,
    locale: props.locale,
    turn_index: props.turnIndex,
    question: props.question,
    question_length: props.questionLength,
  })
}

export function trackLearnAssistantAnswered(props: {
  pagePath: string
  turnIndex: number
  latencyMs: number
  answerLength: number
}): void {
  if (!initialized) return
  posthog.capture('learn_assistant_answered', {
    flow: 'learn_assistant',
    page_path: props.pagePath,
    turn_index: props.turnIndex,
    latency_ms: props.latencyMs,
    answer_length: props.answerLength,
  })
}

export function trackLearnAssistantFailed(props: {
  pagePath: string
  turnIndex: number
  errorKind: LearnAssistantErrorKind
}): void {
  if (!initialized) return
  posthog.capture('learn_assistant_failed', {
    flow: 'learn_assistant',
    page_path: props.pagePath,
    turn_index: props.turnIndex,
    error_kind: props.errorKind,
  })
}

export function trackLearnAssistantClosed(props: { pagePath: string; turns: number; durationMs: number }): void {
  if (!initialized) return
  posthog.capture('learn_assistant_closed', {
    flow: 'learn_assistant',
    page_path: props.pagePath,
    turns: props.turns,
    duration_ms: props.durationMs,
  })
}

// ============================================================================
// Discord community links
// ============================================================================

/**
 * Every place a "Join the Discord" / "Ask on Discord" link lives:
 * - `top_bar`: the account dropdown menu.
 * - `canvas_checklist`: the "Getting started with MCP" canvas checklist.
 * - `onboarding`: the motion onboarding render-room ("Hear it here first").
 * - `learn_header`: the fixed Learn/Docs header Discord button.
 * - `learn_sidebar`: the Learn/Docs sidebar Discord row.
 * - `docs_chat_fallback`: the docs assistant fallback after a failed answer.
 * - `marketing_footer`: the public marketing footer social link.
 */
export type DiscordLinkSource =
  | 'top_bar'
  | 'canvas_checklist'
  | 'onboarding'
  | 'learn_header'
  | 'learn_sidebar'
  | 'docs_chat_fallback'
  | 'marketing_footer'

type DiscordLinkSurface = 'app' | 'onboarding' | 'learn_docs' | 'marketing'

function getDiscordLinkSurface(source: DiscordLinkSource): DiscordLinkSurface {
  switch (source) {
    case 'top_bar':
    case 'canvas_checklist':
      return 'app'
    case 'onboarding':
      return 'onboarding'
    case 'learn_header':
    case 'learn_sidebar':
    case 'docs_chat_fallback':
      return 'learn_docs'
    case 'marketing_footer':
      return 'marketing'
  }
}

/** Where on a model showcase page the board link sat. */
export type ModelShowcasePlacement = 'header' | 'hero' | 'still' | 'footer'

/**
 * A model showcase page sent someone to the public test board. These pages are
 * shared on social, so this is the click-through that matters: landing → board.
 * `sendBeacon` because the click navigates away.
 */
export function trackModelShowcaseBoardClicked(props: {
  model: string
  placement: ModelShowcasePlacement
  assetId?: string
}): void {
  if (!initialized) return
  posthog.capture(
    'model_showcase_board_clicked',
    {
      model: props.model,
      placement: props.placement,
      asset_id: props.assetId ?? undefined,
      current_path: typeof window === 'undefined' ? undefined : window.location.pathname,
    },
    { transport: 'sendBeacon' },
  )
}

/**
 * How far down a model showcase page someone got. Fired once per threshold per
 * page load, so `depth_percent` counts are directly comparable to `$pageview`:
 * they answer whether the specs and the closing CTA are ever reached at all.
 */
export function trackModelShowcaseScrolled(props: { model: string; depthPercent: number }): void {
  if (!initialized) return
  posthog.capture('model_showcase_scrolled', {
    model: props.model,
    depth_percent: props.depthPercent,
    current_path: typeof window === 'undefined' ? undefined : window.location.pathname,
  })
}

/**
 * A model showcase surface other than the board CTA. These pages carry a
 * 13-report write-up, a pricing section and a draft-vs-enhanced comparison, and
 * the board click alone can't tell you which of those is doing the work — or
 * whether the write-up is read at all.
 */
export type ModelShowcaseInteraction =
  'report_opened' | 'section_nav' | 'llms_copied' | 'compare_switched' | 'compare_zoomed' | 'take_link'

export function trackModelShowcaseInteraction(props: {
  model: string
  interaction: ModelShowcaseInteraction
  /** Report slug, section id, example name or take id — whichever the interaction names. */
  target?: string
}): void {
  if (!initialized) return
  posthog.capture('model_showcase_interaction', {
    model: props.model,
    interaction: props.interaction,
    target: props.target ?? undefined,
    current_path: typeof window === 'undefined' ? undefined : window.location.pathname,
  })
}

/** A Discord entry point was clicked — breaks down click-through by source in PostHog. */
export function trackDiscordLinkClicked(props: { source: DiscordLinkSource }): void {
  if (!initialized) return
  posthog.capture(
    'discord_link_clicked',
    {
      source: props.source,
      entry_surface: getDiscordLinkSurface(props.source),
      current_path: typeof window === 'undefined' ? undefined : window.location.pathname,
    },
    { transport: 'sendBeacon' },
  )
}

/**
 * A "what to do while your first shot renders" option was clicked in the onboarding
 * animatic modal. Lets us measure whether the inspiration-first options pull their weight
 * vs the MCP push (does the MCP push help or hurt — connect-rate vs second-shot vs bounce).
 */
export function trackOnboardingWaitOptionClicked(props: {
  option: 'see_films' | 'sample_project' | 'connect_mcp'
  completed: boolean
}): void {
  if (!initialized) return
  posthog.capture('onboarding_wait_option_clicked', { option: props.option, completed: props.completed })
}

/** The draft-assets starter prompt was copied to the clipboard. */
export function trackMcpOnboardingPromptCopied(): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_prompt_copied')
}

/** The reward celebration modal was shown (kling + confetti). */
export function trackMcpOnboardingRewardViewed(props: { milestoneKeys: string[]; olives: number }): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_reward_viewed', {
    milestone_keys: props.milestoneKeys,
    olives: props.olives,
  })
}

/** The user dismissed the reward modal, acknowledging the listed rewards. */
export function trackMcpOnboardingRewardAcknowledged(props: { milestoneKeys: string[]; olives: number }): void {
  if (!initialized) return
  posthog.capture('mcp_onboarding_reward_acknowledged', {
    milestone_keys: props.milestoneKeys,
    olives: props.olives,
  })
}

// ============================================================================
// Canvas v3 Release
// ============================================================================

/**
 * Fires when the language intro modal becomes visible.
 */
export function trackLanguageIntroOpened(props: { projectId?: string; targetLocale?: string }): void {
  if (!initialized) return
  posthog.capture('language_intro_opened', {
    project_id: props.projectId,
    target_locale: props.targetLocale,
  })
}

/**
 * Fires when the language intro modal is closed. `action` distinguishes between the
 * user taking the CTA to switch locales vs. dismissing without changing. The CTA
 * action is `switched_to_<targetLocale>` (e.g. `switched_to_ja`, `switched_to_es`, `switched_to_ar`, `switched_to_ko`).
 */
export function trackLanguageIntroDismissed(props: {
  projectId?: string
  action: 'switched_to_ja' | 'switched_to_es' | 'switched_to_ar' | 'switched_to_ko' | 'dismissed'
  targetLocale?: string
}): void {
  if (!initialized) return
  posthog.capture('language_intro_dismissed', {
    project_id: props.projectId,
    action: props.action,
    target_locale: props.targetLocale,
  })
}

/**
 * The canvas-stability litmus funnel's success leg: fires once per page load
 * when the canvas reaches an editable state (the Pixi engine is ready, or the
 * timeline editor mounted in edit mode). The north-star metric is
 * `editor_opened` → `canvas_ready` conversion at 100% — every open must end in
 * an editable canvas regardless of what broke on the way.
 *
 * `panel_version` is pinned to `'v3'` now that Pixi is the only canvas; it
 * stays on the event for one release so the existing dashboards don't break.
 */
export function trackCanvasReady(props: {
  projectId: string
  panelVersion: 'v3'
  msToReady?: number
  /** Whole open, from the navigation the user started; `null` when that start is unknown. */
  msSinceNavigation?: number | null
  openOrigin?: ProjectOpenOrigin
}): void {
  if (!initialized) return
  posthog.capture('canvas_ready', {
    project_id: props.projectId,
    panel_version: props.panelVersion,
    ms_to_ready: props.msToReady,
    ms_since_navigation: props.msSinceNavigation ?? undefined,
    open_origin: props.openOrigin,
  })
}

export type CanvasErrorReason = 'terminal_failure' | 'circuit_breaker' | 'hard_crash'

/**
 * The litmus test's honest failure leg: the recovery ladder ran out of rungs
 * and `CanvasErrorPanel` replaced the canvas. Fires once per panel mount and
 * — deliberately — instead of `canvas_ready`, so the funnel counts these
 * visits as the failures they are. Volume here sizes the population whose
 * hardware cannot run the engine at all.
 */
export function trackCanvasErrorShown(props: {
  projectId?: string
  reason: CanvasErrorReason
  errorMessage?: string
}): void {
  if (!initialized) return
  posthog.capture('canvas_error_shown', {
    project_id: props.projectId,
    reason: props.reason,
    error_message: props.errorMessage,
    thumbnail_renderer: PIXI_NO_STENCIL_MASK ? 'rounded-mesh' : 'legacy-mask',
    release: currentAnalyticsReleaseSha(),
  })
}

/**
 * A DOM overlay layer inside Canvas v3 threw during render and was hidden by
 * its CanvasOverlayBoundary — the canvas itself stayed editable. This is the
 * litmus test's degradation sidecar: conversions can stay at 100% while
 * overlays silently rot, so this series must trend to zero independently.
 */
export function trackCanvasOverlayDegraded(props: {
  overlay: string
  projectId?: string
  errorMessage?: string
}): void {
  if (!initialized) return
  posthog.capture('canvas_overlay_degraded', {
    overlay: props.overlay,
    project_id: props.projectId,
    error_message: props.errorMessage,
  })
}

/**
 * The V3 crash circuit breaker tripped: the same crash fingerprint recurred
 * enough times inside the window that automatic re-enable is paused until
 * expiry or a new build ships. One event per trip, not per suppressed visit.
 */
export function trackCanvasV3CircuitBreakerTripped(props: {
  projectId?: string
  fingerprint: string
  crashCount: number
  release?: string
}): void {
  if (!initialized) return
  posthog.capture('canvas_v3_circuit_breaker_tripped', {
    project_id: props.projectId,
    fingerprint: props.fingerprint,
    crash_count: props.crashCount,
    release: props.release,
  })
}

/**
 * A project's local IndexedDB Yjs replica failed to replay (corrupt store)
 * and was wiped; the doc resyncs fresh from the server. `phase` distinguishes
 * detection (marker set, reload pending) from the actual wipe on next boot.
 */
export function trackCanvasLocalDocReset(props: { projectId: string; phase: 'detected' | 'wiped' }): void {
  if (!initialized) return
  posthog.capture('canvas_local_doc_reset', {
    project_id: props.projectId,
    phase: props.phase,
  })
}

function currentAnalyticsReleaseSha(): string | undefined {
  try {
    return Sentry.getClient()?.getOptions().release ?? undefined
  } catch {
    return undefined
  }
}

export type CanvasGridToggleSource = 'settings' | 'shortcut'

/**
 * Fires when the user toggles the canvas grid-dot overlay. `source`
 * distinguishes the preferences-menu switch from the keyboard shortcut
 * so we can see how each surface gets used.
 */
export function trackCanvasGridToggled(props: { enabled: boolean; source: CanvasGridToggleSource }): void {
  if (!initialized) return
  posthog.capture('canvas_grid_toggled', {
    enabled: props.enabled,
    source: props.source,
  })
}

export function trackBinCollapseToggled(props: { collapsed: boolean; childCount: number }): void {
  if (!initialized) return
  posthog.capture('bin_collapse_toggled', {
    collapsed: props.collapsed,
    child_count: props.childCount,
  })
}

/** Which store action removed the bins: the single-bin delete or a multi-select removal. */
export type CanvasBinDeleteAction = 'remove_canvas_bin' | 'remove_nodes'

/**
 * A canvas bin left the Yjs document from this browser. MCP's delete_bins is
 * already tracked (mcp_tool_called), so with this every bin deletion has an
 * author — an agent reporting "my bins disappeared" can be answered from
 * events instead of a code audit.
 */
export function trackCanvasBinDeleted(props: {
  action: CanvasBinDeleteAction
  binIds: string[]
  childCount: number
  projectId: string | null
}): void {
  if (!initialized) return
  posthog.capture('canvas_bin_deleted', {
    action: props.action,
    bin_ids: props.binIds,
    bin_count: props.binIds.length,
    child_count: props.childCount,
    project_id: props.projectId ?? undefined,
  })
}

/** How the user moved the camera via the minimap. */
export type CanvasMinimapNavigateMethod = 'click' | 'drag' | 'fit'

export function trackCanvasMinimapToggled(props: { enabled: boolean }): void {
  if (!initialized) return
  posthog.capture('canvas_minimap_toggled', { enabled: props.enabled })
}

export function trackCanvasMinimapNavigated(props: { method: CanvasMinimapNavigateMethod }): void {
  if (!initialized) return
  posthog.capture('canvas_minimap_navigated', { method: props.method })
}

/**
 * Browser file-drop fallback on /blender-import: `started` when a drop begins,
 * `failed` (with a short reason) when upload or import errors out.
 */
export function trackBlenderWebImport(status: 'started' | 'failed', reason?: string): void {
  if (!initialized) return
  posthog.capture('blender_web_import', { status, ...(reason ? { reason } : {}) })
}

// ============================================================================
// Chat (AI assistant pane)
// ============================================================================

export type ChatPaneWidthMode = 'quarter' | 'half'

type ChatEventScope = { projectId: string; teamId?: string | null }

function chatEventScope(props: ChatEventScope): { project_id: string; $groups?: { team: string } } {
  return { project_id: props.projectId, ...(props.teamId ? { $groups: { team: props.teamId } } : {}) }
}

export function trackChatPaneOpened(props: ChatEventScope): void {
  if (!initialized) return
  posthog.capture('chat_pane_opened', chatEventScope(props))
}

export function trackChatPaneClosed(props: ChatEventScope): void {
  if (!initialized) return
  posthog.capture('chat_pane_closed', chatEventScope(props))
}

export function trackChatPaneWidthToggled(props: ChatEventScope & { widthMode: ChatPaneWidthMode }): void {
  if (!initialized) return
  posthog.capture('chat_pane_width_toggled', {
    ...chatEventScope(props),
    width_mode: props.widthMode,
  })
}

export function trackChatMessageSent(props: ChatEventScope & { modelId: string }): void {
  if (!initialized) return
  posthog.capture('chat_message_sent', {
    ...chatEventScope(props),
    model_id: props.modelId,
  })
}

export function trackChatThreadHistoryOpened(props: ChatEventScope): void {
  if (!initialized) return
  posthog.capture('chat_thread_history_opened', chatEventScope(props))
}

export function trackChatThreadSelected(props: ChatEventScope): void {
  if (!initialized) return
  posthog.capture('chat_thread_selected', chatEventScope(props))
}

export function trackChatThreadCreated(props: ChatEventScope): void {
  if (!initialized) return
  posthog.capture('chat_thread_created', chatEventScope(props))
}

export function trackChatToolApprovalResponded(props: {
  projectId: string
  teamId?: string | null
  toolName: string
  approved: boolean
  /** True when auto-approved by the pane's bypass-approvals mode (no manual click). */
  auto?: boolean
}): void {
  if (!initialized) return
  posthog.capture('chat_tool_approval_responded', {
    ...chatEventScope(props),
    tool_name: props.toolName,
    approved: props.approved,
    auto: props.auto ?? false,
  })
}

// ============================================================================
// Library Events
// ============================================================================
// Every library event carries workspace context: 'personal' or the team org
// id. Team workspaces also set $groups so events roll up under the team.

// A ghost session (an admin viewing a customer's workspace) must never emit
// library product events for that workspace: reads would count admin
// inspection as customer usage, and mutations either no-op in the store
// (read-only ghost) or aren't customer behavior. Guarded HERE so every call
// site — store facade and UI components alike — is covered.
function isGhostLibraryWorkspace(workspaceId: string): boolean {
  return (
    getActiveLibraryGhostSession()?.targetOrgId === workspaceId || getActiveGhostSession()?.targetOrgId === workspaceId
  )
}

function libraryWorkspaceProps(workspaceId: string): Record<string, unknown> {
  return workspaceId === 'personal'
    ? { workspace_kind: 'personal' }
    : { workspace_kind: 'team', workspace_id: workspaceId, $groups: { team: workspaceId } }
}

/**
 * Stock-level counts of one workspace's library, fired once per session when
 * the store hydrates (any surface — /library page or the editor panel). This
 * is the "how big are libraries / are tags & properties actually used"
 * denominator; the action events below measure flow.
 */
export function trackLibrarySnapshot(props: {
  workspaceId: string
  collectionCount: number
  assetCount: number
  imageCount: number
  videoCount: number
  fragmentCount: number
  assetsWithHistory: number
  assetsWithValues: number
  propertyCount: number
  collectionsWithTags: number
  tagVocabCount: number
  linkedProjectCount: number
  savedViewCount: number
  folderCount: number
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_snapshot', {
    collection_count: props.collectionCount,
    asset_count: props.assetCount,
    image_count: props.imageCount,
    video_count: props.videoCount,
    fragment_count: props.fragmentCount,
    assets_with_history: props.assetsWithHistory,
    assets_with_values: props.assetsWithValues,
    property_count: props.propertyCount,
    collections_with_tags: props.collectionsWithTags,
    tag_vocab_count: props.tagVocabCount,
    linked_project_count: props.linkedProjectCount,
    saved_view_count: props.savedViewCount,
    folder_count: props.folderCount,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export type LibraryPanelOpenSource = 'tab' | 'toast' | 'reveal' | 'intro'

/** The in-editor library panel was opened (the /library page is a $pageview). */
export function trackLibraryPanelOpened(props: { workspaceId: string; source: LibraryPanelOpenSource }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_panel_opened', {
    source: props.source,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export type LibraryAddSource = 'upload' | 'canvas' | 'text_fragment' | 'bulk' | 'subject_migration'

/** Assets landed in the library (community packs fire library_pack_imported instead). */
export function trackLibraryAssetsAdded(props: {
  workspaceId: string
  source: LibraryAddSource
  count: number
  imageCount: number
  videoCount: number
  textCount: number
  /** Provenance of canvas captures ('generation' | 'upload' | …). */
  assetSourceKind?: string
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_assets_added', {
    source: props.source,
    count: props.count,
    image_count: props.imageCount,
    video_count: props.videoCount,
    text_count: props.textCount,
    asset_source_kind: props.assetSourceKind,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export function trackLibraryPackImported(props: {
  workspaceId: string
  packCount: number
  collectionCount: number
  assetCount: number
  propertyCount: number
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_pack_imported', {
    pack_count: props.packCount,
    collection_count: props.collectionCount,
    asset_count: props.assetCount,
    property_count: props.propertyCount,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** `auto` = created implicitly by a capture/upload ("Saved"/"Uploads"). */
export function trackLibraryCollectionCreated(props: {
  workspaceId: string
  source: 'manual' | 'auto' | 'subject_migration'
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_collection_created', {
    source: props.source,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export type LibraryCollectionEditAction = 'rename' | 'delete' | 'move' | 'set_tags' | 'set_description' | 'set_hero'

export function trackLibraryCollectionEdited(props: {
  workspaceId: string
  action: LibraryCollectionEditAction
  tagCount?: number
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_collection_edited', {
    action: props.action,
    tag_count: props.tagCount,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export type LibraryAssetEditAction = 'rename' | 'move' | 'delete' | 'replace_media' | 'restore_version' | 'edit_text'

export function trackLibraryAssetEdited(props: {
  workspaceId: string
  action: LibraryAssetEditAction
  count: number
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_asset_edited', {
    action: props.action,
    count: props.count,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export function trackLibraryFolderEdited(props: { workspaceId: string; action: 'create' | 'rename' | 'delete' }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_folder_edited', {
    action: props.action,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export function trackLibraryPropertyCreated(props: { workspaceId: string; propertyType: string }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_property_created', {
    property_type: props.propertyType,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export type LibraryPropertyEditAction = 'rename' | 'set_type' | 'delete' | 'add_option' | 'rename_option'

export function trackLibraryPropertyEdited(props: {
  workspaceId: string
  action: LibraryPropertyEditAction
  propertyType?: string
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_property_edited', {
    action: props.action,
    property_type: props.propertyType,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** A property value was set (or cleared) on an asset — the faceting signal.
 * `count` > 1 = one gesture applied to a multi-selection. */
export function trackLibraryPropertyValueSet(props: {
  workspaceId: string
  propertyType?: string
  cleared: boolean
  count?: number
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_property_value_set', {
    property_type: props.propertyType,
    cleared: props.cleared,
    count: props.count ?? 1,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** Collections enabled for a project changed. `enable` = single add (the @-menu
 * inline confirm / save flows); `set` = the panel's choose-collections grid. */
export function trackLibraryProjectLinkChanged(props: {
  workspaceId: string
  projectId: string
  action: 'enable' | 'set'
  enabledCount: number
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_project_link_changed', {
    project_id: props.projectId,
    action: props.action,
    enabled_count: props.enabledCount,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export function trackLibrarySavedViewEdited(props: {
  workspaceId: string
  action: 'save' | 'update' | 'delete'
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_saved_view_edited', {
    action: props.action,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** Inspector layout switch. `surface`: full-width /library page vs editor panel. */
export function trackLibraryViewChanged(props: {
  workspaceId: string
  layout: string
  surface: 'page' | 'panel'
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_view_changed', {
    layout: props.layout,
    surface: props.surface,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** A facet value was added in the filter popover. `facet` is 'kind' or a property key. */
export function trackLibraryFilterApplied(props: { workspaceId: string; facet: string }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_filter_applied', {
    facet: props.facet,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** Debounced: one event per settled non-empty search in the library rail. */
export function trackLibrarySearched(props: { workspaceId: string; queryLength: number }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_search_used', {
    query_length: props.queryLength,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export type LibraryRefKind = 'asset' | 'collection' | 'fragment'

/** A library item entered a prompt — THE consumption signal ("are people
 * actually referencing their library"). */
export function trackLibraryRefInserted(props: {
  workspaceId: string
  /** mention/paste = prompt editor; drop = drag onto an element slot; picker = "Add element" popover. */
  via: 'mention' | 'paste' | 'drop' | 'picker'
  refKind: LibraryRefKind
  modality: 'video' | 'image'
}): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_ref_inserted', {
    via: props.via,
    ref_kind: props.refKind,
    modality: props.modality,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export function trackLibraryTagCopied(props: { workspaceId: string; refKind: 'collection' | 'asset' }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_tag_copied', {
    ref_kind: props.refKind,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

export function trackLibraryAssetDownloaded(props: { workspaceId: string; assetKind: string }): void {
  if (!initialized || isGhostLibraryWorkspace(props.workspaceId)) return
  posthog.capture('library_asset_downloaded', {
    asset_kind: props.assetKind,
    ...libraryWorkspaceProps(props.workspaceId),
  })
}

/** Where a comment thread is being read: canvas pin, timeline marker, or the lightbox lane. */
export type CommentSurface = 'canvas' | 'timeline' | 'lightbox'

/** A reviewer follows a timecode link in a comment thread. */
export function trackCommentTimecodeClicked(props: {
  projectId?: string | null
  surface: CommentSurface
  clock: 'sequence' | 'asset'
  status: 'ok'
}): void {
  if (!initialized) return
  posthog.capture('comment_timecode_clicked', {
    project_id: props.projectId ?? undefined,
    surface: props.surface,
    clock: props.clock,
    status: props.status,
  })
}

/** A picker selection inserts a reference into a comment draft. */
export function trackCommentReferenceInserted(props: { kind: CommentRefKind }): void {
  if (!initialized) return
  posthog.capture('comment_reference_inserted', { kind: props.kind })
}

/** A reviewer follows a board reference in a comment. */
export function trackCommentReferenceClicked(props: { kind: CommentRefKind; exists: boolean }): void {
  if (!initialized) return
  posthog.capture('comment_reference_clicked', { kind: props.kind, exists: props.exists })
}

export type LineageLinkVia = 'suggestion' | 'drop' | 'picker' | 'outside'

/**
 * A person linked an asset to its source in the Lineage section (docs/asset-lineage-design.md
 * §7.4) — the UI counterpart of the MCP `mcp_lineage_declared`. `via` says how: a confirmed
 * file-name suggestion, a node dropped on the slot, the board picker, or a file/URL outside
 * Martini. Never includes the note, the file name or the URL.
 */
export function trackLineageSourceLinked(props: {
  projectId: string
  assetId: string
  via: LineageLinkVia
  operation: string
  recorded: boolean
  droppedInputs: number
}): void {
  if (!initialized) return
  posthog.capture('lineage_source_linked', {
    project_id: props.projectId,
    asset_id: props.assetId,
    via: props.via,
    operation: props.operation,
    recorded: props.recorded,
    dropped_inputs: props.droppedInputs,
  })
}

/** A file-name suggestion was turned down ("Not this") — the false-positive rate of the matcher. */
export function trackLineageSuggestionDismissed(props: { projectId: string; assetId: string }): void {
  if (!initialized) return
  posthog.capture('lineage_suggestion_dismissed', { project_id: props.projectId, asset_id: props.assetId })
}

/** Outcome of a recognized canvas-node paste; never includes clipboard content. */
export function trackCanvasPasteOutcome(props: {
  projectId: string
  scope: 'same_project' | 'cross_project'
  itemCount: number
  status: 'success' | 'failed'
  failureCategory?: 'allocation_failed' | 'unexpected_error'
}): void {
  if (!initialized) return
  try {
    posthog.capture('canvas_paste_outcome', {
      project_id: props.projectId,
      scope: props.scope,
      item_count: props.itemCount,
      status: props.status,
      failure_category: props.failureCategory ?? undefined,
    })
  } catch {
    // Telemetry must never interrupt paste completion or its failure notice.
  }
}

/** Colour adjustments copied into the in-memory Look clipboard. */
export function trackLookCopied(): void {
  if (!initialized) return
  posthog.capture('look_copied')
}

/** All requests settled; succeeded is the number of authored shots. */
export function trackLookPasted(props: { targetCount: number; succeeded: number }): void {
  if (!initialized) return
  posthog.capture('look_pasted', { target_count: props.targetCount, succeeded: props.succeeded })
}

// ---------------------------------------------------------------------------
// Lineage (the view, the report, export, "Match a file", the canvas duplicate check)
// ---------------------------------------------------------------------------

/** Where a person entered lineage from. */
export type LineageViewSource = 'context_menu' | 'panel' | 'report'
export type LineageReportLinkSource = 'settings' | 'timeline_menu' | 'panel' | 'check_file_result' | 'duplicate_notice'
export type LineageReportView = 'tree' | 'list'
export type LineageViewAction =
  | 'recentred'
  | 'hop_opened'
  | 'path_revealed'
  | 'details_opened'
  | 'finder_opened'
  | 'finder_picked'
  | 'breadcrumb_jumped'
  | 'zoomed'
  | 'opened_on_canvas'
  | 'only_related'
export type LineageReportAction =
  | 'view_switched'
  | 'family_expanded'
  | 'chip_jumped'
  | 'finder_picked'
  | 'filter_changed'
  | 'sort_changed'
  | 'show_more'
  | 'history_opened'
  | 'refreshed'
  | 'prompts_expanded'
  | 'csv_downloaded'
export type LineageExportFormatLabel = 'package' | 'report'
export type LineageExportFailure = 'refused' | 'request_failed' | 'job_failed' | 'timed_out'
export type LineageCheckFileSource = 'command_palette' | 'page' | 'report_header'
export type LineageCheckFileResult = 'hit_in_project' | 'hit_other_project' | 'hit_unopenable' | 'miss' | 'failed'
export type LineageDuplicateResult = 'none' | 'same_canvas' | 'other_project' | 'failed'
export type LineageDuplicateNoticeAction = 'show_original' | 'upload_anyway' | 'open_lineage' | 'dismissed'

/** Coarse, low-cardinality byte sizes for files and exports. */
export function sizeBucket(bytes: number | null | undefined): string | undefined {
  if (bytes == null || !Number.isFinite(bytes)) return undefined
  if (bytes < 1_000_000) return '<1MB'
  if (bytes < 10_000_000) return '1-10MB'
  if (bytes < 100_000_000) return '10-100MB'
  if (bytes < 1_000_000_000) return '100MB-1GB'
  return '>1GB'
}

/** `image` / `video` / `audio` from a MIME type; `other` for anything else. */
export function mediaKindOf(mimeType: string | null | undefined): 'image' | 'video' | 'audio' | 'other' {
  const kind = mimeType?.split('/')[0]
  return kind === 'image' || kind === 'video' || kind === 'audio' ? kind : 'other'
}

interface LineageViewSession {
  id: string
  projectId: string
  openedAt: number
  counts: Partial<Record<LineageViewAction, number>>
  /** Cards whose path was already revealed this session: one `path_revealed` per card. */
  revealed: Set<string>
  zoomed: boolean
  finderOpened: boolean
  trailDepthMax: number
  ledger: 'story' | 'lineage' | null
}
let lineageView: LineageViewSession | null = null

/** The lineage view opened; every `lineage_view_*` event until it closes shares the returned `session_id`. */
export function trackLineageViewOpened(props: { projectId: string; assetId: string; source: LineageViewSource }): void {
  lineageView = {
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()),
    projectId: props.projectId,
    openedAt: Date.now(),
    counts: {},
    revealed: new Set(),
    zoomed: false,
    finderOpened: false,
    trailDepthMax: 0,
    ledger: null,
  }
  if (!initialized) return
  posthog.capture('lineage_view_opened', {
    session_id: lineageView.id,
    project_id: props.projectId,
    asset_id: props.assetId,
    source: props.source,
  })
}

/** Which ledger the open view read; recorded on the session summary. */
export function noteLineageViewLedger(ledger: 'story' | 'lineage'): void {
  if (lineageView) lineageView.ledger = ledger
}

/**
 * One interaction in the open view. `path_revealed` is counted once per card (`cardId`), `zoomed` and
 * `finder_opened` once per session — the summary carries the tallies; the per-event stream stays sane.
 */
export function trackLineageViewInteracted(
  action: LineageViewAction,
  props: { cardId?: string; trailDepth?: number; method?: string; value?: string } = {},
): void {
  const session = lineageView
  if (!session) return
  if (action === 'path_revealed') {
    if (!props.cardId || session.revealed.has(props.cardId)) return
    session.revealed.add(props.cardId)
  }
  if (action === 'zoomed') {
    if (session.zoomed) return
    session.zoomed = true
  }
  if (action === 'finder_opened') {
    if (session.finderOpened) return
    session.finderOpened = true
  }
  session.counts[action] = (session.counts[action] ?? 0) + 1
  if (props.trailDepth !== undefined) session.trailDepthMax = Math.max(session.trailDepthMax, props.trailDepth)
  if (!initialized) return
  posthog.capture('lineage_view_interacted', {
    session_id: session.id,
    project_id: session.projectId,
    action,
    method: props.method ?? undefined,
    value: props.value ?? undefined,
  })
}

/** The view closed (any exit, including going to a node on the canvas): the session summary. */
export function trackLineageViewClosed(): void {
  const session = lineageView
  lineageView = null
  if (!session || !initialized) return
  const c = session.counts
  posthog.capture('lineage_view_closed', {
    session_id: session.id,
    project_id: session.projectId,
    duration_ms: Date.now() - session.openedAt,
    ledger: session.ledger ?? undefined,
    cards_recentred: c.recentred ?? 0,
    hops_opened: c.hop_opened ?? 0,
    paths_revealed: c.path_revealed ?? 0,
    details_opened: c.details_opened ?? 0,
    finder_picks: c.finder_picked ?? 0,
    breadcrumb_jumps: c.breadcrumb_jumped ?? 0,
    trail_depth_max: session.trailDepthMax,
    zoomed: session.zoomed,
    opened_on_canvas: (c.opened_on_canvas ?? 0) > 0,
    navigated: Object.values(c).some(count => count > 0),
  })
}

/** A link into a project's lineage report was clicked (the report's `$pageview` follows; this names the door). */
export function trackLineageReportLinkClicked(props: {
  source: LineageReportLinkSource
  projectId?: string | null
}): void {
  if (!initialized) return
  posthog.capture('lineage_report_link_clicked', { source: props.source, project_id: props.projectId ?? undefined })
}

let lineageReport: { projectId: string | null; view: LineageReportView } = { projectId: null, view: 'tree' }

/**
 * The report page's project, from the route, before anything on it is interactive: an interaction
 * while the placements still load must name this project, not the last one's.
 */
export function noteLineageReportProject(projectId: string | null): void {
  if (lineageReport.projectId !== projectId) lineageReport = { projectId, view: lineageReport.view }
}

/** The report page's open view, read by every `lineage_report_interacted` until it changes. */
export function noteLineageReportView(view: LineageReportView): void {
  lineageReport.view = view
}

/** The report page rendered its content: which view, and how much there is to read. */
export function trackLineageReportViewed(props: {
  projectId: string
  view: LineageReportView
  ledgerV2: boolean
  familyCount: number | null
  assetCount: number | null
  runCount: number
}): void {
  // `view` here is the one the report opened on; interactions keep the open view the page noted,
  // which may have changed while the content loaded.
  if (lineageReport.projectId !== props.projectId)
    lineageReport = { projectId: props.projectId, view: lineageReport.view }
  if (!initialized) return
  posthog.capture('lineage_report_viewed', {
    project_id: props.projectId,
    view: props.view,
    ledger_v2: props.ledgerV2,
    family_count: props.familyCount ?? undefined,
    asset_count: props.assetCount ?? undefined,
    run_count: props.runCount,
  })
}

/** One interaction on the report page. `value` is a filter/sort key or the view switched to, never a name. */
export function trackLineageReportInteracted(action: LineageReportAction, value?: string): void {
  if (!initialized) return
  posthog.capture('lineage_report_interacted', {
    project_id: lineageReport.projectId ?? undefined,
    view: lineageReport.view,
    action,
    value: value ?? undefined,
  })
}

export function trackLineageExportDialogOpened(props: { projectId: string }): void {
  if (!initialized) return
  posthog.capture('lineage_export_dialog_opened', { project_id: props.projectId })
}

export function trackLineageExportStarted(props: {
  projectId: string
  format: LineageExportFormatLabel
  includeHidden: boolean
}): void {
  if (!initialized) return
  posthog.capture('lineage_export_started', {
    project_id: props.projectId,
    format: props.format,
    include_hidden: props.includeHidden,
  })
}

export function trackLineageExportCompleted(props: {
  projectId: string
  format: LineageExportFormatLabel
  includeHidden: boolean
  durationMs: number
  sizeBytes: number | null
}): void {
  if (!initialized) return
  posthog.capture('lineage_export_completed', {
    project_id: props.projectId,
    format: props.format,
    include_hidden: props.includeHidden,
    duration_ms: Math.round(props.durationMs),
    size_bucket: sizeBucket(props.sizeBytes),
  })
}

export function trackLineageExportFailed(props: {
  projectId: string
  format: LineageExportFormatLabel
  durationMs: number
  reason: LineageExportFailure
}): void {
  if (!initialized) return
  posthog.capture('lineage_export_failed', {
    project_id: props.projectId,
    format: props.format,
    duration_ms: Math.round(props.durationMs),
    reason: props.reason,
  })
}

/** What was done with a finished export: the file taken, or its fingerprint copied. */
export function trackLineageExportAction(props: {
  projectId: string
  format: LineageExportFormatLabel
  action: 'auto_download' | 'download' | 'fingerprint_copied'
}): void {
  if (!initialized) return
  posthog.capture('lineage_export_action', {
    project_id: props.projectId,
    format: props.format,
    action: props.action,
  })
}

/** "Match a file" appeared (the ⌘K dialog, the /lineage/check page, or the report header's popover). */
export function trackLineageCheckFileOpened(props: {
  source: LineageCheckFileSource
  projectId?: string | null
}): void {
  if (!initialized) return
  posthog.capture('lineage_check_file_opened', { source: props.source, project_id: props.projectId ?? undefined })
}

/** A file was hashed and matched. Never the file name or the hash — only kind, size bucket, timings, outcome. */
export function trackLineageCheckFileChecked(props: {
  source: LineageCheckFileSource
  projectId?: string | null
  mimeType: string
  sizeBytes: number
  hashingMs: number
  lookupMs: number | null
  result: LineageCheckFileResult
  placeCount: number
}): void {
  if (!initialized) return
  posthog.capture('lineage_check_file_checked', {
    source: props.source,
    project_id: props.projectId ?? undefined,
    media_kind: mediaKindOf(props.mimeType),
    size_bucket: sizeBucket(props.sizeBytes),
    hashing_ms: Math.round(props.hashingMs),
    lookup_ms: props.lookupMs === null ? undefined : Math.round(props.lookupMs),
    result: props.result,
    place_count: props.placeCount,
  })
}

/** A canvas drop was checked against known bytes; `same_canvas` / `other_project` mean a notice was shown, `failed` shows none. */
export function trackLineageDuplicateChecked(props: {
  projectId: string
  result: LineageDuplicateResult
  /** The answer came after the upload had already started (the hash missed the budget or the hit was elsewhere). */
  late: boolean
  placedCopy: boolean
  mimeType: string
  sizeBytes: number
  hashingMs: number | null
}): void {
  if (!initialized) return
  posthog.capture('lineage_duplicate_checked', {
    project_id: props.projectId,
    result: props.result,
    late: props.late,
    placed_copy: props.placedCopy,
    media_kind: mediaKindOf(props.mimeType),
    size_bucket: sizeBucket(props.sizeBytes),
    hashing_ms: props.hashingMs === null ? undefined : Math.round(props.hashingMs),
  })
}

/** What a person did with the "you already have this" notice; a notice that times out fires nothing. */
export function trackLineageDuplicateNoticeAction(props: {
  projectId: string
  kind: 'same_canvas' | 'other_project'
  action: LineageDuplicateNoticeAction
}): void {
  if (!initialized) return
  posthog.capture('lineage_duplicate_notice_action', {
    project_id: props.projectId,
    kind: props.kind,
    action: props.action,
  })
}
